<template>
  <div class="grid grid-cols-12 gap-4">
    <!-- Scan Complete / Skipped Popup -->
    <transition name="popup-slide">
      <div
        v-if="scanCompleteUrl || scanSkippedMessage"
        class="fixed top-5 right-5 z-50 max-w-sm rounded-lg border px-4 py-3 shadow-lg"
        :class="
          scanSkippedMessage
            ? 'border-amber-200 bg-amber-50'
            : 'border-green-200 bg-green-50'
        "
      >
        <div class="flex items-start gap-3">
          <AlertTriangle
            v-if="scanSkippedMessage"
            class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
          />
          <CheckCircle2
            v-else
            class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"
          />
          <div class="flex-1">
            <p
              class="text-sm font-semibold"
              :class="scanSkippedMessage ? 'text-amber-800' : 'text-green-800'"
            >
              {{ scanSkippedMessage || "Scan Completed, please click the URL" }}
            </p>
            <a
              v-if="!scanSkippedMessage && scanCompleteUrl"
              :href="scanCompleteUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-green-700 underline hover:text-green-900 break-all"
            >
              {{ scanCompleteUrl }}
            </a>
          </div>
          <button
            @click="dismissPopup"
            class="text-lg leading-none"
            :class="
              scanSkippedMessage
                ? 'text-amber-700 hover:text-amber-900'
                : 'text-green-700 hover:text-green-900'
            "
            aria-label="Dismiss message"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Left: Main Results Table -->
    <div class="col-span-12 bg-white p-8">
      <div class="mb-4 flex justify-between items-center">
        <h2 class="text-lg font-semibold">
          Scanned Pages
          <span class="text-sm font-normal text-gray-500 ml-2">
            ({{ crawledCount }} crawled{{
              discoveredCount > 0 ? ", " + discoveredCount + " discovered" : ""
            }})
          </span>
        </h2>
        <div class="flex items-center gap-2">
          <button
            @click="exportToCSV"
            class="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700"
          >
            Export CSV
          </button>
        </div>
      </div>

      <!-- Per-column Filters -->
      <div class="grid grid-cols-6 gap-2 mb-2 text-xs">
        <select
          v-model="filters.titleStatus"
          class="border p-1 rounded filters text-base"
        >
          <option value="">All Titles</option>
          <option value="ideal">Ideal</option>
          <option value="short">Too Short</option>
          <option value="long">Too Long</option>
          <option value="missing">Missing</option>
        </select>
        <select
          v-model="filters.metaStatus"
          class="border p-1 rounded filters text-base"
        >
          <option value="">All Meta</option>
          <option value="valid">Valid</option>
          <option value="missing">Missing</option>
          <option value="duplicate">Duplicate</option>
        </select>
        <select
          v-model="filters.https"
          class="border p-1 rounded filters text-base"
        >
          <option value="">All HTTPS</option>
          <option value="Yes">HTTPS</option>
          <option value="No">HTTP</option>
        </select>
        <select
          v-model="filters.duplicateTitle"
          class="border p-1 rounded filters text-base"
        >
          <option value="">Duplicate Title?</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <select
          v-model="filters.duplicateMainContent"
          class="border p-1 rounded filters text-base"
        >
          <option value="">Duplicate Content?</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search URL..."
          class="border p-1 px-2 rounded col-span-2 text-base"
        />
      </div>

      <!-- Results Table -->
      <div class="overflow-y-auto max-h-[420px] border rounded">
        <table class="w-full text-base">
          <thead class="sticky top-0 z-10">
            <tr>
              <th class="px-2 py-1 border border-gray-200">#</th>
              <th
                class="px-2 py-1 border border-gray-200 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                @click="toggleSort('url')"
              >
                URL
                <ChevronUp
                  v-if="sortColumn === 'url' && sortDirection === 'asc'"
                  class="inline w-3 h-3 text-blue-600"
                />
                <ChevronDown
                  v-else-if="sortColumn === 'url' && sortDirection === 'desc'"
                  class="inline w-3 h-3 text-blue-600"
                />
              </th>
              <th
                class="px-2 py-1 border border-gray-200 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                @click="toggleSort('title')"
              >
                Title
                <ChevronUp
                  v-if="sortColumn === 'title' && sortDirection === 'asc'"
                  class="inline w-3 h-3 text-blue-600"
                />
                <ChevronDown
                  v-else-if="sortColumn === 'title' && sortDirection === 'desc'"
                  class="inline w-3 h-3 text-blue-600"
                />
              </th>
              <th
                class="px-2 py-1 border border-gray-200 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                @click="toggleSort('description')"
              >
                Meta Desc
                <ChevronUp
                  v-if="sortColumn === 'description' && sortDirection === 'asc'"
                  class="inline w-3 h-3 text-blue-600"
                />
                <ChevronDown
                  v-else-if="
                    sortColumn === 'description' && sortDirection === 'desc'
                  "
                  class="inline w-3 h-3 text-blue-600"
                />
              </th>
              <th
                class="px-2 py-1 border border-gray-200 cursor-pointer select-none hover:bg-blue-100 transition-colors w-20"
                @click="toggleSort('https')"
              >
                HTTPS
                <ChevronUp
                  v-if="sortColumn === 'https' && sortDirection === 'asc'"
                  class="inline w-3 h-3 text-blue-600"
                />
                <ChevronDown
                  v-else-if="sortColumn === 'https' && sortDirection === 'desc'"
                  class="inline w-3 h-3 text-blue-600"
                />
              </th>
              <th
                class="px-2 py-1 border border-gray-200 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                @click="toggleSort('status')"
              >
                Status
                <ChevronUp
                  v-if="sortColumn === 'status' && sortDirection === 'asc'"
                  class="inline w-3 h-3 text-blue-600"
                />
                <ChevronDown
                  v-else-if="
                    sortColumn === 'status' && sortDirection === 'desc'
                  "
                  class="inline w-3 h-3 text-blue-600"
                />
              </th>
              <!-- <th class="px-2 py-1">Status</th> -->
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 5" :key="n" class="border-b">
                <td class="px-2 py-1 text-center">
                  <div class="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
                </td>
                <td class="px-2 py-1 text-center">
                  <div
                    class="animate-pulse bg-gray-300 h-4 w-full rounded"
                  ></div>
                </td>
                <td class="px-2 py-1 text-center">
                  <div
                    class="animate-pulse bg-gray-300 h-4 w-full rounded"
                  ></div>
                </td>
                <td class="px-2 py-1 text-center">
                  <div
                    class="animate-pulse bg-gray-300 h-4 w-full rounded"
                  ></div>
                </td>
                <td class="px-2 py-1 text-center">
                  <div class="animate-pulse bg-gray-300 h-4 w-16 rounded"></div>
                </td>
                <td class="px-2 py-1 text-center">
                  <div class="animate-pulse bg-gray-300 h-4 w-12 rounded"></div>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr
                v-for="(page, index) in sortedResults"
                :key="index"
                class="hover:bg-blue-50 border-b"
                :class="{
                  'cursor-pointer': page.crawled !== false,
                  'opacity-70': page.crawled === false,
                }"
              >
                <td class="px-2 py-1 text-center border border-gray-200">
                  {{ index + 1 }}
                </td>
                <td
                  class="px-2 py-1 text-center text-blue-600 underline break-all border border-gray-200"
                  :class="{ 'cursor-pointer': page.crawled !== false }"
                  @click="page.crawled !== false && selectPage(index)"
                >
                  <span
                    v-tippy="
                      page.crawled !== false
                        ? {
                            content: 'Click to view results',
                            theme: 'diagnostic',
                            placement: 'top',
                          }
                        : { disabled: true }
                    "
                  >
                    {{ page.url }}
                  </span>
                </td>
                <td
                  class="px-2 py-1 text-center border border-gray-200"
                  :class="{ 'cursor-pointer': page.crawled !== false }"
                  @click="page.crawled !== false && selectPage(index)"
                >
                  {{ page.title || "—" }}
                </td>
                <td
                  class="px-2 py-1 text-center border border-gray-200"
                  :class="{ 'cursor-pointer': page.crawled !== false }"
                  @click="page.crawled !== false && selectPage(index)"
                >
                  {{ page.description || "—" }}
                </td>
                <td
                  class="px-2 py-1 text-center border border-gray-200 w-24"
                  :class="{ 'cursor-pointer': page.crawled !== false }"
                  @click="page.crawled !== false && selectPage(index)"
                >
                  {{ page.hasHttps ? "Yes" : "No" }}
                </td>
                <td
                  class="px-2 py-1 text-center border border-gray-200 w-24"
                  :class="{ 'cursor-pointer': page.crawled !== false }"
                  @click="page.crawled !== false && selectPage(index)"
                >
                  <span
                    v-if="
                      page.crawled === false &&
                      (page.scan_status === 'skipped' ||
                        page.scan_status === 'blocked' ||
                        page.scan_status === 'failed')
                    "
                    v-tippy="{
                      content:
                        page.scan_message ||
                        (page.scan_status === 'blocked'
                          ? 'This page blocked the scan request.'
                          : 'This page was not crawled.'),
                      theme: 'diagnostic',
                      placement: 'top',
                    }"
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium cursor-help"
                    :class="
                      page.scan_status === 'blocked' ||
                      page.scan_status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                    "
                  >
                    {{
                      page.scan_status === "blocked"
                        ? "⊘ Blocked"
                        : page.scan_status === "failed"
                        ? "⊘ Failed"
                        : "⊘ Skipped"
                    }}
                  </span>
                  <span
                    v-else-if="page.crawled === false"
                    v-tippy="{
                      content:
                        resultsStillProcessing && scanningUrl !== page.url
                          ? 'Certain Results are still processing'
                          : '',
                      theme: 'diagnostic',
                      placement: 'top',
                    }"
                  >
                    <button
                      @click.stop="scanSinglePage(page.url)"
                      :disabled="resultsStillProcessing"
                      class="text-white px-2 py-0.5 rounded text-xs inline-flex items-center gap-1"
                      :class="
                        resultsStillProcessing
                          ? 'bg-green-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      "
                    >
                      <svg
                        v-if="scanningUrl === page.url"
                        class="animate-spin h-3 w-3 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      <span v-if="scanningUrl === page.url">Scanning...</span>
                      <span v-else>Scan</span>
                    </button>
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    <Check class="w-3 h-3" /> Crawled
                  </span>
                </td>
              </tr>
              <tr v-if="filteredResults.length === 0">
                <td colspan="6" class="px-4 py-8 text-center text-gray-400">
                  No pages match the current filters.
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Bottom Panel: Tabbed Page Details -->
      <div
        v-if="selectedPage !== null"
        ref="detailsSection"
        class="mt-4 border rounded p-3 bg-gray-50"
      >
        <!-- <div class="mb-2">
          <h3 class="font-semibold text-sm" v-if="currentPage?.url">
            Details for:
            <span class="text-blue-600 break-all">{{
              currentPage?.url || "N/A"
            }}</span>
          </h3>
        </div> -->
        <div class="flex text-sm">
          <button
            v-for="tab in tabs"
            :key="tab"
            @click="activeTab = tab"
            :class="[
              'px-3 py-1 rounded-t-md',
              activeTab === tab
                ? 'bg-white border-t border-l border-r'
                : 'hover:bg-gray-300',
            ]"
          >
            {{ tab }}
          </button>
        </div>

        <!-- Tab Content -->
        <div class="text-sm" v-if="currentPage">
          <div v-if="activeTab === 'Meta Data'">
            <div class="metadata bg-gray-50 p-4 rounded-md border">
              <h3 class="text-base font-semibold mb-2">Metadata Validation</h3>

              <!-- Meta Title Validation -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Meta Title Test</p>
                <p>
                  <strong>Text: </strong>
                  <span v-if="currentPage.title">{{
                    currentPage?.title || "No Meta Title Found"
                  }}</span>
                  <span v-else class="text-gray-400">No Meta Title Found</span>
                </p>
                <p>
                  <strong>Length: </strong>
                  <span v-if="currentPage.title_length">
                    {{ currentPage?.title_length || 0 }} characters
                  </span>
                  <span v-else class="text-gray-400">No Data</span>
                </p>
                <p v-if="!currentPage.title" class="text-red-500 font-bold">
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No Meta Title detected. Ensure your page has a proper title
                  for better SEO.
                </p>
                <p
                  v-if="currentPage.is_title_duplicate"
                  class="text-red-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Duplicate Page Title Found
                </p>
                <p
                  v-if="
                    currentPage.title_length < 50 &&
                    currentPage.title_length > 0
                  "
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Title is Too Short
                </p>
                <p
                  v-if="currentPage.title_length > 60"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Title is Too Long
                </p>
                <p
                  v-if="
                    currentPage.title_length >= 50 &&
                    currentPage.title_length <= 60
                  "
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Title is in the Ideal Range
                </p>
              </div>

              <!-- Meta Description Validation -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Meta Description Test</p>
                <p>
                  <strong>Text: </strong>
                  <span v-if="currentPage.description">
                    {{ currentPage.description }}
                  </span>
                  <span v-else class="text-gray-400"
                    >No Meta Description Found</span
                  >
                </p>
                <p>
                  <strong>Length: </strong>
                  <span v-if="currentPage.description_length">
                    {{ currentPage.description_length }} characters
                  </span>
                  <span v-else class="text-gray-400">No Data</span>
                </p>
                <p
                  v-if="!currentPage.description"
                  class="text-red-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No Meta Description detected. Add a brief summary of your page
                  for better SEO.
                </p>
                <p
                  v-if="
                    currentPage.description_length < 150 &&
                    currentPage.description_length > 0
                  "
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Meta Description is Too Short
                </p>
                <p
                  v-if="currentPage.description_length > 220"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Meta Description is Too Long
                </p>
                <p
                  v-if="
                    currentPage.description_length >= 150 &&
                    currentPage.description_length <= 220
                  "
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Meta Description is in the Ideal Range
                </p>
              </div>

              <!-- Duplicate Main Content Validation -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Duplicate Content Test</p>
                <p
                  v-if="currentPage.is_main_content_duplicate"
                  class="text-red-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Duplicate Content Detected — the content of this page is
                  identical to the following page(s):
                </p>
                <ul
                  v-if="
                    currentPage.is_main_content_duplicate &&
                    currentPage.duplicate_main_content_urls
                  "
                  class="list-disc ml-5 mt-1 text-red-600 text-xs"
                >
                  <li
                    v-for="(
                      dupUrl, idx
                    ) in currentPage.duplicate_main_content_urls"
                    :key="idx"
                  >
                    <a
                      :href="dupUrl"
                      target="_blank"
                      class="underline break-all"
                      >{{ dupUrl }}</a
                    >
                  </li>
                </ul>
                <p
                  v-if="currentPage.is_main_content_duplicate"
                  class="text-red-600 text-xs mt-1"
                >
                  Duplicate content can dilute your SEO rankings. Consider
                  consolidating pages or differentiating the content.
                </p>
                <p
                  v-if="!currentPage.is_main_content_duplicate"
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Main Content is Unique
                </p>
              </div>

              <!-- H1 Tag Validation -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">H1 Tag Test</p>
                <p>
                  <strong>Text: </strong>
                  <span v-if="currentPage.h1_text">{{
                    currentPage.h1_text
                  }}</span>
                  <span v-else class="text-gray-400">No H1 Tag Found</span>
                </p>
                <p>
                  <strong>Count: </strong>
                  <span
                    >{{ currentPage.h1_count ?? 0 }} H1 tag{{
                      currentPage.h1_count !== 1 ? "s" : ""
                    }}</span
                  >
                </p>
                <p>
                  <strong>Length: </strong>
                  <span v-if="currentPage.h1_count > 0">
                    {{ currentPage.h1_length ?? 0 }} characters
                  </span>
                  <span v-else class="text-gray-400">No Data</span>
                </p>
                <p
                  v-if="currentPage.h1_count === 0"
                  class="text-red-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No H1 tag detected. Every page should have exactly one H1
                  heading for optimal SEO.
                </p>
                <p
                  v-if="currentPage.h1_count > 1"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Multiple H1 tags detected ({{ currentPage.h1_count }}). A page
                  should have only one H1. Use H2-H6 for subheadings.
                </p>
                <p
                  v-if="currentPage.h1_count > 0 && currentPage.h1_length > 70"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  H1 tag is too long ({{ currentPage.h1_length }} chars). Keep
                  H1 headings concise — ideally under 70 characters.
                </p>
                <p
                  v-if="
                    currentPage.h1_count === 1 && currentPage.h1_length <= 70
                  "
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  H1 Tag is Valid
                </p>
              </div>

              <!-- Google Site Verification Tag -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Google Site Verification Test</p>
                <p>
                  <strong>Status: </strong>
                  <span
                    v-if="currentPage.google_site_verification"
                    class="text-green-600 font-bold"
                  >
                    <span class="inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-4 h-4" /> Verification Tag
                      Found</span
                    >
                  </span>
                  <span v-else class="text-red-500 font-bold">
                    <span class="inline-flex items-center gap-1"
                      ><AlertTriangle class="w-4 h-4" /> No Verification Tag
                      Found</span
                    >
                  </span>
                </p>
                <p v-if="currentPage.google_site_verification">
                  <strong>Verification Code: </strong>
                  <span class="text-xs text-gray-600 break-all">{{
                    currentPage.google_site_verification
                  }}</span>
                </p>
                <p
                  v-if="!currentPage.has_google_verification"
                  class="text-red-500 font-bold mt-1 text-xs"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No Google site verification meta tag detected. Add a
                  <code class="bg-gray-200 px-1 rounded"
                    >&lt;meta name="google-site-verification"&gt;</code
                  >
                  tag to verify site ownership in Google Search Console.
                </p>
                <p v-else class="text-green-600 font-bold mt-1 text-xs">
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Google site verification tag is present.
                </p>
              </div>

              <!-- Responsive Design Test -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Responsive Design Test</p>
                <p>
                  <strong>Viewport Meta Tag: </strong>
                  <span
                    v-if="currentPage.has_viewport_meta"
                    class="text-green-600 font-bold inline-flex items-center gap-1"
                  >
                    <CheckCircle2 class="w-4 h-4" /> Found
                  </span>
                  <span
                    v-else
                    class="text-red-500 font-bold inline-flex items-center gap-1"
                  >
                    <AlertTriangle class="w-4 h-4" /> Missing
                  </span>
                </p>
                <p v-if="currentPage.viewport_content">
                  <strong>Content: </strong>
                  <code class="text-xs bg-gray-100 px-1 rounded break-all">{{
                    currentPage.viewport_content
                  }}</code>
                </p>
                <p>
                  <strong>Mobile Responsive: </strong>
                  <span
                    v-if="currentPage.is_mobile_responsive"
                    class="text-green-600 font-bold inline-flex items-center gap-1"
                  >
                    <CheckCircle2 class="w-4 h-4" /> Yes
                  </span>
                  <span
                    v-else
                    class="text-red-500 font-bold inline-flex items-center gap-1"
                  >
                    <AlertTriangle class="w-4 h-4" /> Not responsive
                  </span>
                </p>
                <p
                  v-if="!currentPage.is_mobile_responsive"
                  class="text-red-500 font-bold mt-1 text-xs"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  The viewport meta tag is missing or not configured for
                  responsive design. Add
                  <code class="bg-gray-200 px-1 rounded"
                    >&lt;meta name="viewport" content="width=device-width,
                    initial-scale=1"&gt;</code
                  >
                  inside the &lt;head&gt; for mobile-friendly pages.
                </p>
                <p v-else class="text-green-600 font-bold mt-1 text-xs">
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  This page uses responsive design principles.
                </p>
              </div>

              <!-- URL Structure Validation -->
              <div class="p-3 bg-white shadow-md rounded-md">
                <p class="font-bold">URL Structure Test</p>
                <p>
                  <strong>URL: </strong>
                  <span class="text-blue-600 break-all text-xs">{{
                    currentPage.url
                  }}</span>
                </p>
                <p>
                  <strong>Length: </strong>
                  <span> {{ currentPage.url_length || 0 }} characters </span>
                </p>
                <p
                  v-if="currentPage.url_length && currentPage.url_length > 60"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  URL is Too Long ({{ currentPage.url_length }} chars). Ideal
                  length is 50-60 characters.
                </p>
                <p
                  v-if="
                    currentPage.url_length &&
                    currentPage.url_length >= 50 &&
                    currentPage.url_length <= 60
                  "
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  URL is in the Ideal Range
                </p>
                <p
                  v-if="currentPage.url_has_underscores"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  URL Contains Underscores. Use hyphens (-) instead.
                </p>
                <p
                  v-if="currentPage.url_has_ampersands"
                  class="text-blue-500 font-bold"
                >
                  <Info class="w-4 h-4 inline-block mr-1 align-text-bottom" />
                  URL Contains Ampersands (&)
                </p>
                <p
                  v-if="currentPage.url_has_special_chars"
                  class="text-yellow-500 font-bold"
                >
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  URL Contains Special Characters or Uppercase Letters
                </p>
                <p
                  v-if="
                    !currentPage.url_has_underscores &&
                    !currentPage.url_has_ampersands &&
                    !currentPage.url_has_special_chars &&
                    currentPage.url_length &&
                    currentPage.url_length <= 60
                  "
                  class="text-green-500 font-bold"
                >
                  <CheckCircle2
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  URL Structure Looks Clean
                </p>
              </div>
            </div>

            <!-- Optional: still keep the issues panel below -->
          </div>

          <div v-else-if="activeTab === 'Performance'">
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading performance results...</p>
              </div>
            </div>
            <div
              v-else-if="
                (performanceData.status === 'Failed' ||
                  performanceData.mobile?.error) &&
                !performanceData.mobile?.score
              "
              class="text-center py-10 text-gray-500"
            >
              <p class="text-red-500 text-sm">
                {{
                  performanceData._error ||
                  performanceData.error ||
                  performanceData.mobile?.error ||
                  "Performance scan failed."
                }}
              </p>
              <p class="text-xs text-gray-400 mt-2">
                Ensure
                <code class="bg-gray-100 px-1 rounded"
                  >npm install lighthouse</code
                >
                and
                <code class="bg-gray-100 px-1 rounded"
                  >npx playwright install chromium</code
                >
                were run in the backend folder.
              </p>
              <button
                type="button"
                @click="$emit('re-scan')"
                class="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm transition-colors cursor-pointer"
              >
                <RefreshCw class="w-3.5 h-3.5 mr-1" /> Retry Performance Scan
              </button>
            </div>
            <div v-else>
              <div class="grid grid-cols-2 gap-4 mb-6">
                <div
                  class="p-5 rounded-lg border bg-white text-center shadow-sm"
                >
                  <h3
                    class="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center gap-1.5"
                  >
                    <Smartphone class="w-4 h-4 text-gray-600" /> Mobile
                  </h3>
                  <div
                    class="inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold mb-2"
                    :class="{
                      'bg-green-100 text-green-700':
                        performanceData.mobile?.scoreClass === 'fast',
                      'bg-yellow-100 text-yellow-700':
                        performanceData.mobile?.scoreClass === 'moderate',
                      'bg-red-100 text-red-700':
                        performanceData.mobile?.scoreClass === 'slow',
                      'bg-gray-100 text-gray-500':
                        !performanceData.mobile?.scoreClass,
                    }"
                  >
                    {{ performanceData.mobile?.score ?? "—" }}
                  </div>
                  <p class="text-xs font-medium capitalize text-gray-600">
                    {{ performanceData.mobile?.scoreClass || "N/A" }}
                  </p>
                </div>
                <div
                  class="p-5 rounded-lg border bg-white text-center shadow-sm"
                >
                  <h3
                    class="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center gap-1.5"
                  >
                    <Monitor class="w-4 h-4 text-gray-600" /> Desktop
                  </h3>
                  <div
                    class="inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold mb-2"
                    :class="{
                      'bg-green-100 text-green-700':
                        performanceData.desktop?.scoreClass === 'fast',
                      'bg-yellow-100 text-yellow-700':
                        performanceData.desktop?.scoreClass === 'moderate',
                      'bg-red-100 text-red-700':
                        performanceData.desktop?.scoreClass === 'slow',
                      'bg-gray-100 text-gray-500':
                        !performanceData.desktop?.scoreClass,
                    }"
                  >
                    {{ performanceData.desktop?.score ?? "—" }}
                  </div>
                  <p class="text-xs font-medium capitalize text-gray-600">
                    {{ performanceData.desktop?.scoreClass || "N/A" }}
                  </p>
                </div>
              </div>

              <div
                class="p-4 bg-white border rounded-md shadow-sm"
                v-if="performanceData.mobile?.metrics"
              >
                <h4 class="text-base font-semibold mb-3">Core Web Vitals</h4>
                <div class="overflow-x-auto">
                  <table class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50 border-b">
                        <th class="text-left px-2 py-1.5 font-semibold">
                          Metric
                        </th>
                        <th class="text-center px-2 py-1.5 font-semibold w-24">
                          Mobile
                        </th>
                        <th class="text-center px-2 py-1.5 font-semibold w-24">
                          Desktop
                        </th>
                        <th class="text-center px-2 py-1.5 font-semibold w-28">
                          Threshold
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(label, key) in {
                          firstContentfulPaint: 'First Contentful Paint',
                          largestContentfulPaint: 'Largest Contentful Paint',
                          cumulativeLayoutShift: 'Cumulative Layout Shift',
                          timeToInteractive: 'Time to Interactive',
                          timeToFirstByte: 'Time to First Byte',
                          speedIndex: 'Page Load Time',
                        }"
                        :key="key"
                        class="border-b"
                      >
                        <td class="px-2 py-1.5 font-medium">{{ label }}</td>
                        <td class="px-2 py-1.5 text-center">
                          <span
                            :class="{
                              'text-green-600':
                                performanceData.mobile?.metrics?.[key]?.pass,
                              'text-red-500':
                                performanceData.mobile?.metrics?.[key] &&
                                !performanceData.mobile?.metrics?.[key]?.pass,
                            }"
                            class="font-mono"
                          >
                            {{
                              performanceData.mobile?.metrics?.[key]
                                ?.displayValue || "—"
                            }}
                          </span>
                        </td>
                        <td class="px-2 py-1.5 text-center">
                          <span
                            :class="{
                              'text-green-600':
                                performanceData.desktop?.metrics?.[key]?.pass,
                              'text-red-500':
                                performanceData.desktop?.metrics?.[key] &&
                                !performanceData.desktop?.metrics?.[key]?.pass,
                            }"
                            class="font-mono"
                          >
                            {{
                              performanceData.desktop?.metrics?.[key]
                                ?.displayValue || "—"
                            }}
                          </span>
                        </td>
                        <td
                          class="px-2 py-1.5 text-center text-gray-500 text-xxs"
                        >
                          {{
                            key === "cumulativeLayoutShift"
                              ? "≤ 0.1"
                              : key === "timeToFirstByte"
                              ? "≤ 200ms"
                              : key === "firstContentfulPaint"
                              ? "≤ 1.2s / 1.8s"
                              : key === "timeToInteractive"
                              ? "≤ 3.0s / 4.0s"
                              : "≤ 2.5s / 4.3s"
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div
                class="p-4 bg-white border rounded-md shadow-sm mt-4"
                v-if="performanceData.mobile?.renderBlockingResources"
              >
                <h4 class="text-base font-semibold mb-3">
                  Render-Blocking Resources
                </h4>
                <div class="grid grid-cols-2 gap-3 text-xs">
                  <div class="p-3 bg-gray-50 rounded">
                    <span class="font-semibold">Mobile:</span>
                    <span
                      :class="{
                        'text-green-600':
                          performanceData.mobile?.renderBlockingResources?.pass,
                        'text-red-500':
                          !performanceData.mobile?.renderBlockingResources
                            ?.pass,
                      }"
                      class="ml-2 font-mono font-semibold"
                    >
                      {{
                        performanceData.mobile?.renderBlockingResources
                          ?.count ?? "—"
                      }}
                      resource(s)
                    </span>
                  </div>
                  <div class="p-3 bg-gray-50 rounded">
                    <span class="font-semibold">Desktop:</span>
                    <span
                      :class="{
                        'text-green-600':
                          performanceData.desktop?.renderBlockingResources
                            ?.pass,
                        'text-red-500':
                          !performanceData.desktop?.renderBlockingResources
                            ?.pass,
                      }"
                      class="ml-2 font-mono font-semibold"
                    >
                      {{
                        performanceData.desktop?.renderBlockingResources
                          ?.count ?? "—"
                      }}
                      resource(s)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'SOP Compliance'">
            <!-- 1. Quick Summary: SOP Compliance Overview -->
            <div class="p-4 mb-4 bg-gray-50 border rounded-md">
              <h3 class="font-semibold text-sm text-gray-700 mb-2">
                SOP Compliance Overview
              </h3>
              <p class="text-xs text-gray-500">
                Below is a quick summary of your compliance status. Items in
                <span class="text-red-500">red</span> need attention.
              </p>

              <!-- Progress Bar -->
              <div class="mt-2 bg-gray-200 h-2 w-full rounded-full">
                <div
                  class="bg-green-500 h-2 rounded-full"
                  :style="{ width: sopCompliancePercentage + '%' }"
                ></div>
              </div>
              <p class="mt-1 text-xs text-gray-700">
                {{ sopCompliancePassed }}/{{ sopComplianceTotal }} checks passed
                ({{ sopCompliancePercentage }}%)
              </p>
            </div>

            <!-- 2. Grouped Sections -->
            <div class="space-y-4">
              <!-- Contact Info -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-lg font-bold text-gray-800 mb-3">
                  Contact Info
                </h4>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <!-- Phone Numbers -->
                  <div>
                    <strong>Phone Numbers: &nbsp;</strong>
                    <span
                      v-for="(num, index) in currentPage.phone_numbers"
                      :key="index"
                      :class="{
                        'text-green-600': currentPage.sop?.phone_format_valid,
                        'text-red-500':
                          currentPage.sop?.phone_format_valid === false,
                      }"
                    >
                      {{ num
                      }}<span
                        v-if="index < currentPage.phone_numbers.length - 1"
                        >,
                      </span>
                    </span>
                  </div>

                  <!-- Email Address -->
                  <div>
                    <strong>Email Address: &nbsp;</strong>
                    <span
                      :class="{
                        'text-green-600': currentPage.sop?.email_format_valid,
                        'text-red-500':
                          currentPage.sop?.email_format_valid === false,
                      }"
                    >
                      {{ currentPage.email_address || "—" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Company Info -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-lg font-bold text-gray-800 mb-3">
                  Company Info
                </h4>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <!-- Company Name -->
                  <div>
                    <strong>Company Name: &nbsp;</strong>
                    <span
                      :class="{
                        'text-green-600': currentPage.sop?.company_name_valid,
                        'text-red-500':
                          currentPage.sop?.company_name_valid === false,
                      }"
                    >
                      {{ currentPage.company_name || "—" }}
                    </span>
                  </div>

                  <!-- Address -->
                  <div>
                    <strong>Address: &nbsp;</strong>
                    <span
                      :class="{
                        'text-green-600': currentPage.sop?.address_format_valid,
                        'text-red-500':
                          currentPage.sop?.address_format_valid === false,
                      }"
                    >
                      {{ currentPage.address || "—" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Legal -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-lg font-bold text-gray-800 mb-3">Legal</h4>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <!-- Removed Privacy Policy -->
                  <div>
                    <strong>Copyright: &nbsp;</strong>
                    <span
                      :class="{
                        'text-green-600': currentPage.sop?.copyright_year_valid,
                        'text-red-500':
                          currentPage.sop?.copyright_year_valid === false,
                      }"
                    >
                      {{ currentPage.copyright || "—" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Functional Checks Section -->
              <h4 class="text-lg font-bold text-gray-800 mb-3">
                Functional Checks
              </h4>

              <!-- Back to Top -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-lg font-bold text-gray-800 mb-3">Navigation</h4>
                <div class="text-sm">
                  <div>
                    <strong>Back to Top Button: &nbsp;</strong>
                    <span
                      :class="{
                        'text-green-600': currentPage.sop?.back_to_top_valid,
                        'text-red-500':
                          currentPage.sop?.back_to_top_valid === false,
                      }"
                    >
                      {{
                        currentPage.back_to_top?.status === "Passed"
                          ? "Found"
                          : "Not Found"
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- External Links target="_blank" Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedExternalLinks = !collapsedExternalLinks"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedExternalLinks }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      External Links
                    </h4>
                    <strong class="text-sm text-gray-600"
                      >target="_blank" Check</strong
                    >
                    <span
                      v-if="currentPage.sop?.external_links_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Passed</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" /> Some
                      Failed</span
                    >
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedExternalLinks" class="mt-3">
                  <div
                    v-if="
                      !currentPage.external_links_validation ||
                      currentPage.external_links_validation.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No external links detected on this page.
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleExternalLinksSort('extUrl')"
                        >
                          URL
                          <ChevronUp
                            v-if="
                              externalLinksSortColumn === 'extUrl' &&
                              externalLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              externalLinksSortColumn === 'extUrl' &&
                              externalLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleExternalLinksSort('extStatus')"
                        >
                          Status
                          <ChevronUp
                            v-if="
                              externalLinksSortColumn === 'extStatus' &&
                              externalLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              externalLinksSortColumn === 'extStatus' &&
                              externalLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedExternalLinks"
                        :key="'ext-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.url }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><CheckCircle2
                                class="w-3.5 h-3.5 text-green-600"
                              />
                              Passed</span
                            >
                          </span>
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><XCircle class="w-3.5 h-3.5 text-red-500" />
                              Failed</span
                            >
                          </span>
                        </td>
                      </tr>
                      <tr
                        v-for="(item, idx) in paginatedExternalLinks.filter(
                          (i) => i.status === 'Failed'
                        )"
                        :key="'ereason-' + idx"
                      >
                        <!-- <td
                          colspan="2"
                          class="px-2 py-1 text-red-600 bg-red-50 border text-xs"
                        >
                          <span class="inline-flex items-center gap-1"><AlertTriangle class="w-3.5 h-3.5 text-red-600 flex-shrink-0" /> {{ item.reason }}</span>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                  <!-- External Links Pagination -->
                  <div
                    v-if="externalLinksTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.externalLinks--"
                      :disabled="sopPagination.externalLinks === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.externalLinks }} of
                      {{ externalLinksTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.externalLinks++"
                      :disabled="
                        sopPagination.externalLinks >= externalLinksTotalPages
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- Local Link Detector — staging domains must not appear on live sites -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedLocalLinks = !collapsedLocalLinks"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedLocalLinks }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Local Link Detector
                    </h4>
                    <strong class="text-sm text-gray-600"
                      >staging-domain check</strong
                    >
                    <span
                      v-if="currentPage.sop?.local_links_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Clear</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" />
                      Found</span
                    >
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedLocalLinks" class="mt-3">
                  <div
                    v-if="
                      !currentPage.local_links_validation ||
                      currentPage.local_links_validation.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No staging/local links detected on this page.
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleLocalLinksSort('url')"
                        >
                          URL
                        </th>
                        <th
                          class="text-left px-2 py-1 border w-48 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleLocalLinksSort('host')"
                        >
                          Matched Host
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleLocalLinksSort('tag')"
                        >
                          Tag
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleLocalLinksSort('status')"
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedLocalLinks"
                        :key="'local-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.url }}
                        </td>
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.matched_host }}
                        </td>
                        <td class="px-2 py-1 border text-center text-gray-600">
                          &lt;{{ item.tag }}&gt;
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><CheckCircle2
                                class="w-3.5 h-3.5 text-green-600"
                              />
                              Passed</span
                            >
                          </span>
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><XCircle class="w-3.5 h-3.5 text-red-500" />
                              Failed</span
                            >
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Local Links Pagination -->
                  <div
                    v-if="localLinksTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.localLinks--"
                      :disabled="sopPagination.localLinks === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.localLinks }} of
                      {{ localLinksTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.localLinks++"
                      :disabled="
                        sopPagination.localLinks >= localLinksTotalPages
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- Profanity / Bad Words Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedProfanity = !collapsedProfanity"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedProfanity }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Profanity & Bad Words
                    </h4>
                    <span
                      v-if="
                        !currentPage.profanity_validation ||
                        currentPage.profanity_validation.length === 0
                      "
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Clear</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" />
                      {{ currentPage.profanity_validation.length }} Found</span
                    >
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedProfanity" class="mt-3">
                  <div
                    v-if="
                      !currentPage.profanity_validation ||
                      currentPage.profanity_validation.length === 0
                    "
                    class="text-xs text-gray-500 py-1"
                  >
                    No profanity, vulgarities, or inappropriate terms detected
                    on this page.
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleProfanitySort('word')"
                        >
                          Flagged Word / Phrase
                        </th>
                        <th
                          class="text-left px-2 py-1 border w-28 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleProfanitySort('category')"
                        >
                          Category
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-24 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleProfanitySort('severity')"
                        >
                          Severity
                        </th>
                        <th class="text-left px-2 py-1 border">
                          Context Snippet
                        </th>
                        <th class="text-left px-2 py-1 border">Suggestion</th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="toggleProfanitySort('status')"
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedProfanity"
                        :key="'profanity-' + idx"
                        class="border-b hover:bg-gray-50"
                      >
                        <td
                          class="px-2 py-1 border text-red-600 font-mono font-bold break-all"
                        >
                          "{{ item.word }}"
                        </td>
                        <td class="px-2 py-1 border text-gray-700 capitalize">
                          {{ item.category }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            class="px-2 py-0.5 rounded text-xxs font-semibold uppercase"
                            :class="{
                              'bg-red-100 text-red-700':
                                item.severity === 'high',
                              'bg-yellow-100 text-yellow-700':
                                item.severity === 'medium',
                              'bg-blue-100 text-blue-700':
                                item.severity === 'low',
                            }"
                          >
                            {{ item.severity }}
                          </span>
                        </td>
                        <td
                          class="px-2 py-1 border text-gray-600 font-mono text-xxs break-words"
                        >
                          <span v-if="item.context"
                            >"...{{ item.context }}..."</span
                          >
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-2 py-1 border text-gray-700">
                          {{ item.suggestion || "Remove or replace" }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            class="text-red-500 font-semibold cursor-help"
                            v-tippy="{
                              content:
                                item.reason ||
                                'Inappropriate language detected.',
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><XCircle class="w-3.5 h-3.5 text-red-500" />
                              Failed</span
                            >
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Profanity Pagination -->
                  <div
                    v-if="profanityTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.profanity--"
                      :disabled="sopPagination.profanity === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span>
                      Page {{ sopPagination.profanity }} of
                      {{ profanityTotalPages }}
                    </span>
                    <button
                      @click="sopPagination.profanity++"
                      :disabled="sopPagination.profanity >= profanityTotalPages"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- Minified Assets Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedMinified = !collapsedMinified"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedMinified }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Minified Assets (CSS &amp; JS)
                    </h4>
                    <span class="text-sm font-bold text-gray-600"
                      >{{ currentPage.minified_assets?.score ?? 100 }}%</span
                    >
                    <span
                      v-if="currentPage.sop?.minified_assets_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Minified</span
                    >
                    <span
                      v-else-if="
                        (currentPage.minified_assets?.score ?? 100) >= 50
                      "
                      class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-yellow-600" />
                      Partial</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><XCircle class="w-3 h-3 text-red-600" /> Needs
                      Attention</span
                    >
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedMinified" class="mt-3">
                  <!-- Summary stats -->
                  <div class="grid grid-cols-4 gap-2 mb-3 text-xs text-center">
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-gray-700">
                        {{
                          currentPage.minified_assets?.summary?.total_css ?? 0
                        }}
                      </div>
                      <div class="text-gray-500">Total CSS</div>
                    </div>
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-green-600">
                        {{
                          currentPage.minified_assets?.summary?.minified_css ??
                          0
                        }}
                      </div>
                      <div class="text-gray-500">Minified CSS</div>
                    </div>
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-gray-700">
                        {{
                          currentPage.minified_assets?.summary?.total_js ?? 0
                        }}
                      </div>
                      <div class="text-gray-500">Total JS</div>
                    </div>
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-green-600">
                        {{
                          currentPage.minified_assets?.summary?.minified_js ?? 0
                        }}
                      </div>
                      <div class="text-gray-500">Minified JS</div>
                    </div>
                  </div>

                  <!-- No assets found -->
                  <div
                    v-if="
                      !currentPage.minified_assets?.results ||
                      currentPage.minified_assets.results.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No external CSS or JS files detected on this page.
                  </div>

                  <!-- Results table -->
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'minifiedSortColumn',
                              'minifiedSortDirection',
                              'file'
                            )
                          "
                        >
                          File
                          <ChevronUp
                            v-if="
                              minifiedSortColumn === 'file' &&
                              minifiedSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              minifiedSortColumn === 'file' &&
                              minifiedSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-12 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'minifiedSortColumn',
                              'minifiedSortDirection',
                              'type'
                            )
                          "
                        >
                          Type
                          <ChevronUp
                            v-if="
                              minifiedSortColumn === 'type' &&
                              minifiedSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              minifiedSortColumn === 'type' &&
                              minifiedSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'minifiedSortColumn',
                              'minifiedSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              minifiedSortColumn === 'status' &&
                              minifiedSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              minifiedSortColumn === 'status' &&
                              minifiedSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedMinifiedAssets"
                        :key="'min-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.file }}
                        </td>
                        <td class="px-2 py-1 border text-center text-gray-500">
                          {{ item.type }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Minified</span
                          >
                          <span
                            v-else
                            class="text-yellow-500 font-semibold cursor-help"
                            v-tippy="{
                              content: item.message,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><AlertTriangle class="w-3 h-3 text-red-500" />
                              Not Minified</span
                            >
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Minified Pagination -->
                  <div
                    v-if="minifiedTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.minified--"
                      :disabled="sopPagination.minified === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.minified }} of
                      {{ minifiedTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.minified++"
                      :disabled="sopPagination.minified >= minifiedTotalPages"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Contact Link Validation (Email + Telephone) -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedContactLink = !collapsedContactLink"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedContactLink }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Contact Link Validation
                    </h4>
                    <span
                      v-if="
                        currentPage.sop?.email_mailto_valid &&
                        currentPage.sop?.tel_link_valid
                      "
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Passed</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" /> Some
                      Failed</span
                    >
                  </div>

                  <!-- Data View Dropdown -->
                  <select
                    v-model="contactLinkFilter"
                    class="text-xs border p-1 rounded"
                    @click.stop
                  >
                    <option value="all">All Contacts</option>
                    <option value="email">Email Only</option>
                    <option value="tel">Telephone Only</option>
                  </select>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedContactLink" class="mt-3">
                  <!-- Combined validation table -->
                  <table class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'contactLinkSortColumn',
                              'contactLinkSortDirection',
                              'contact'
                            )
                          "
                        >
                          Contact
                          <ChevronUp
                            v-if="
                              contactLinkSortColumn === 'contact' &&
                              contactLinkSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              contactLinkSortColumn === 'contact' &&
                              contactLinkSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'contactLinkSortColumn',
                              'contactLinkSortDirection',
                              'type'
                            )
                          "
                        >
                          Type
                          <ChevronUp
                            v-if="
                              contactLinkSortColumn === 'type' &&
                              contactLinkSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              contactLinkSortColumn === 'type' &&
                              contactLinkSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'contactLinkSortColumn',
                              'contactLinkSortDirection',
                              'href'
                            )
                          "
                        >
                          Href
                          <ChevronUp
                            v-if="
                              contactLinkSortColumn === 'href' &&
                              contactLinkSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              contactLinkSortColumn === 'href' &&
                              contactLinkSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'contactLinkSortColumn',
                              'contactLinkSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              contactLinkSortColumn === 'status' &&
                              contactLinkSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              contactLinkSortColumn === 'status' &&
                              contactLinkSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedContactLinkRows"
                        :key="'contact-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700">
                          {{ item.contact }}
                        </td>
                        <td class="px-2 py-1 border text-gray-500">
                          {{ item.type }}
                        </td>
                        <td
                          class="px-2 py-1 border font-mono text-gray-600 break-all"
                        >
                          <span v-if="item.href">{{ item.href }}</span>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><CheckCircle2
                                class="w-3.5 h-3.5 text-green-600"
                              />
                              Passed</span
                            >
                          </span>
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                          >
                            <span class="inline-flex items-center gap-1"
                              ><XCircle class="w-3.5 h-3.5 text-red-500" />
                              Failed</span
                            >
                          </span>
                        </td>
                      </tr>
                      <tr v-if="contactLinkRows.length === 0">
                        <td
                          colspan="4"
                          class="px-2 py-1 text-gray-500 text-center"
                        >
                          No email addresses or telephone links detected on this
                          page.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Contact Link Pagination -->
                  <div
                    v-if="contactLinkTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.contactLink--"
                      :disabled="sopPagination.contactLink === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.contactLink }} of
                      {{ contactLinkTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.contactLink++"
                      :disabled="
                        sopPagination.contactLink >= contactLinkTotalPages
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Image Filename Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <!-- Clickable summary bar -->
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedImageFilenames = !collapsedImageFilenames"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedImageFilenames }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Image Filename Validation
                    </h4>
                    <span
                      v-if="currentPage.sop?.image_filenames_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Passed</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" /> Some
                      Failed</span
                    >
                  </div>
                </div>

                <!-- Collapsible body -->
                <div v-if="!collapsedImageFilenames" class="mt-3">
                  <div
                    v-if="
                      !currentPage.image_filename_validation ||
                      currentPage.image_filename_validation.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No &lt;img&gt; elements detected on this page.
                  </div>

                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageFilenameSortColumn',
                              'imageFilenameSortDirection',
                              'filename'
                            )
                          "
                        >
                          Filename
                          <ChevronUp
                            v-if="
                              imageFilenameSortColumn === 'filename' &&
                              imageFilenameSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageFilenameSortColumn === 'filename' &&
                              imageFilenameSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th class="text-center px-2 py-1 border w-16">
                          No Underscores
                        </th>
                        <th class="text-center px-2 py-1 border w-16">
                          Valid Ext
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageFilenameSortColumn',
                              'imageFilenameSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              imageFilenameSortColumn === 'status' &&
                              imageFilenameSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageFilenameSortColumn === 'status' &&
                              imageFilenameSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedImageFilenames"
                        :key="'imgfn-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.filename }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.validation.no_underscores"
                            class="text-green-600 inline-flex items-center"
                            ><CheckCircle2 class="w-3.5 h-3.5 text-green-600"
                          /></span>
                          <span
                            v-else
                            class="text-red-500 cursor-help inline-flex items-center"
                            v-tippy="{
                              content:
                                'Contains underscores — use hyphens instead',
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                            ><XCircle class="w-3.5 h-3.5 text-red-500"
                          /></span>
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.validation.valid_extension"
                            class="text-green-600 inline-flex items-center"
                            ><CheckCircle2 class="w-3.5 h-3.5 text-green-600"
                          /></span>
                          <span
                            v-else
                            class="text-red-500 cursor-help inline-flex items-center"
                            v-tippy="{
                              content:
                                'Invalid file extension — use .jpg, .png, .webp, or .svg',
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                            ><XCircle class="w-3.5 h-3.5 text-red-500"
                          /></span>
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Passed</span
                          >
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help inline-flex items-center gap-1"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                            ><XCircle class="w-3 h-3 text-red-600" />
                            Failed</span
                          >
                        </td>
                      </tr>
                      <tr
                        v-for="(item, idx) in paginatedImageFilenames.filter(
                          (i) => i.status === 'Failed'
                        )"
                        :key="'fbreason-' + idx"
                      >
                        <!-- <td
                          colspan="4"
                          class="px-2 py-1 text-red-600 bg-red-50 border text-xs"
                        >
                          <span class="inline-flex items-center gap-1"><AlertTriangle class="w-3.5 h-3.5 text-red-600 flex-shrink-0" /> {{ item.reason }}</span>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                  <!-- Image Filenames Pagination -->
                  <div
                    v-if="imageFilenamesTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.imageFilenames--"
                      :disabled="sopPagination.imageFilenames === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.imageFilenames }} of
                      {{ imageFilenamesTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.imageFilenames++"
                      :disabled="
                        sopPagination.imageFilenames >= imageFilenamesTotalPages
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Image Alt Attribute Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedImageAlt = !collapsedImageAlt"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedImageAlt }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Image Alt Text Validation
                    </h4>
                    <span
                      v-if="currentPage.sop?.image_alt_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Passed</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" /> Some
                      Failed</span
                    >
                  </div>
                </div>

                <div v-if="!collapsedImageAlt" class="mt-3">
                  <div
                    v-if="
                      !currentPage.image_alt_validation ||
                      currentPage.image_alt_validation.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No &lt;img&gt; elements detected on this page.
                  </div>

                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageAltSortColumn',
                              'imageAltSortDirection',
                              'filename'
                            )
                          "
                        >
                          Filename
                          <ChevronUp
                            v-if="
                              imageAltSortColumn === 'filename' &&
                              imageAltSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageAltSortColumn === 'filename' &&
                              imageAltSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th class="text-center px-2 py-1 border w-16">
                          Has Alt?
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageAltSortColumn',
                              'imageAltSortDirection',
                              'alt'
                            )
                          "
                        >
                          Alt Text
                          <ChevronUp
                            v-if="
                              imageAltSortColumn === 'alt' &&
                              imageAltSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageAltSortColumn === 'alt' &&
                              imageAltSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageAltSortColumn',
                              'imageAltSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              imageAltSortColumn === 'status' &&
                              imageAltSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageAltSortColumn === 'status' &&
                              imageAltSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedImageAlt"
                        :key="'imalt-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.filename }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.has_alt && !item.alt_empty"
                            class="text-green-600 inline-flex items-center"
                            ><CheckCircle2 class="w-3.5 h-3.5 text-green-600"
                          /></span>
                          <span
                            v-else
                            class="text-red-500 inline-flex items-center"
                            ><XCircle class="w-3.5 h-3.5 text-red-500"
                          /></span>
                        </td>
                        <td
                          class="px-2 py-1 border text-gray-700 break-all max-w-[200px] truncate"
                        >
                          {{ item.alt_text || "—" }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Passed</span
                          >
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help inline-flex items-center gap-1"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                            ><XCircle class="w-3 h-3 text-red-600" />
                            Failed</span
                          >
                        </td>
                      </tr>
                      <tr
                        v-for="(item, idx) in paginatedImageAlt.filter(
                          (i) => i.status === 'Failed'
                        )"
                        :key="'altreason-' + idx"
                      >
                        <td
                          colspan="4"
                          class="px-2 py-1 text-red-600 bg-red-50 border text-xs"
                        >
                          <span class="inline-flex items-center gap-1"
                            ><AlertTriangle
                              class="w-3.5 h-3.5 text-red-600 flex-shrink-0"
                            />
                            {{ item.reason }}</span
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div
                    v-if="imageAltTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.imageAlt--"
                      :disabled="sopPagination.imageAlt === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.imageAlt }} of
                      {{ imageAltTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.imageAlt++"
                      :disabled="sopPagination.imageAlt >= imageAltTotalPages"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Image File Size Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedImageSize = !collapsedImageSize"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedImageSize }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Image File Size Validation
                    </h4>
                    <span
                      v-if="currentPage.sop?.image_size_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All Under
                      500KB</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-red-600" /> Some
                      Oversized</span
                    >
                  </div>
                </div>

                <div v-if="!collapsedImageSize" class="mt-3">
                  <div
                    v-if="
                      !currentPage.image_size_validation ||
                      currentPage.image_size_validation.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No &lt;img&gt; elements detected on this page.
                  </div>

                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageSizeSortColumn',
                              'imageSizeSortDirection',
                              'filename'
                            )
                          "
                        >
                          Filename
                          <ChevronUp
                            v-if="
                              imageSizeSortColumn === 'filename' &&
                              imageSizeSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageSizeSortColumn === 'filename' &&
                              imageSizeSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageSizeSortColumn',
                              'imageSizeSortDirection',
                              'size'
                            )
                          "
                        >
                          Size (KB)
                          <ChevronUp
                            v-if="
                              imageSizeSortColumn === 'size' &&
                              imageSizeSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageSizeSortColumn === 'size' &&
                              imageSizeSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th class="text-center px-2 py-1 border w-20">
                          Over 500KB?
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-20 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'imageSizeSortColumn',
                              'imageSizeSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              imageSizeSortColumn === 'status' &&
                              imageSizeSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              imageSizeSortColumn === 'status' &&
                              imageSizeSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedImageSize"
                        :key="'imsz-' + idx"
                        class="border-b"
                      >
                        <td class="px-2 py-1 border text-gray-700 break-all">
                          {{ item.filename }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          {{ item.size_kb !== null ? item.size_kb : "—" }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.is_over_500kb"
                            class="text-red-500 inline-flex items-center gap-1"
                            ><XCircle class="w-3.5 h-3.5 text-red-500" />
                            Yes</span
                          >
                          <span
                            v-else-if="item.status === 'Skipped'"
                            class="text-gray-400"
                            >—</span
                          >
                          <span
                            v-else
                            class="text-green-600 inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3.5 h-3.5 text-green-600" />
                            No</span
                          >
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Passed</span
                          >
                          <span
                            v-else-if="item.status === 'Skipped'"
                            class="text-gray-400 font-semibold"
                            >⊘ Skipped</span
                          >
                          <span
                            v-else
                            class="text-red-500 font-semibold cursor-help inline-flex items-center gap-1"
                            v-tippy="{
                              content: item.reason,
                              theme: 'diagnostic',
                              placement: 'top',
                            }"
                            ><XCircle class="w-3 h-3 text-red-600" />
                            Failed</span
                          >
                        </td>
                      </tr>
                      <tr
                        v-for="(item, idx) in paginatedImageSize.filter(
                          (i) => i.status === 'Failed' || i.status === 'Skipped'
                        )"
                        :key="'szreason-' + idx"
                      >
                        <td
                          colspan="4"
                          class="px-2 py-1 bg-red-50 border text-xs"
                          :class="
                            item.status === 'Skipped'
                              ? 'text-gray-500'
                              : 'text-red-600'
                          "
                        >
                          <span class="inline-flex items-center gap-1"
                            ><AlertTriangle
                              class="w-3.5 h-3.5 text-red-600 flex-shrink-0"
                            />
                            {{ item.reason }}</span
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div
                    v-if="imageSizeTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.imageSize--"
                      :disabled="sopPagination.imageSize === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.imageSize }} of
                      {{ imageSizeTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.imageSize++"
                      :disabled="sopPagination.imageSize >= imageSizeTotalPages"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Google Analytics / Tag Manager -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <h4 class="text-lg font-bold text-gray-800">
                      Google Analytics / Tag Manager
                    </h4>
                    <span
                      v-if="currentPage.sop?.google_analytics_valid"
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" />
                      Detected</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><XCircle class="w-3 h-3 text-red-600" /> Not Found</span
                    >
                  </div>
                </div>
                <div class="mt-2 text-sm">
                  <p v-if="currentPage.google_analytics?.detected">
                    <strong>Detected: &nbsp;</strong>
                    <span class="text-green-600">{{
                      currentPage.google_analytics.message
                    }}</span>
                  </p>
                  <p v-else class="text-red-500 flex items-start gap-1">
                    <AlertTriangle class="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{{
                      currentPage.google_analytics?.message ||
                      "No Google Analytics or Tag Manager tracking code detected."
                    }}</span>
                  </p>
                  <p
                    v-if="currentPage.google_analytics?.recommendation"
                    class="text-xs text-gray-600 mt-1"
                  >
                    <span class="inline-flex items-start gap-1"
                      ><Lightbulb
                        class="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5"
                      /><span>{{
                        currentPage.google_analytics.recommendation
                      }}</span></span
                    >
                  </p>
                </div>
              </div>
              <!-- W3C Markup Validation -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedW3C = !collapsedW3C"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedW3C }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      W3C Markup Validation
                    </h4>
                    <span class="text-sm font-bold text-gray-600"
                      >{{ currentPage.w3c_validation?.score ?? 100 }}%</span
                    >
                    <span
                      v-if="
                        (currentPage.w3c_validation?.status ?? 'Passed') ===
                        'Passed'
                      "
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" />
                      Passed</span
                    >
                    <span
                      v-else-if="
                        (currentPage.w3c_validation?.status ?? '') === 'Warning'
                      "
                      class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-yellow-600" />
                      Warning</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><XCircle class="w-3 h-3 text-red-600" /> Failed</span
                    >
                  </div>
                </div>
                <div v-if="!collapsedW3C" class="mt-3">
                  <div class="grid grid-cols-3 gap-2 mb-3 text-xs text-center">
                    <div class="bg-red-50 p-2 rounded">
                      <div class="font-bold text-red-600">
                        {{ currentPage.w3c_validation?.summary?.errors ?? 0 }}
                      </div>
                      <div class="text-gray-500">Errors</div>
                    </div>
                    <div class="bg-yellow-50 p-2 rounded">
                      <div class="font-bold text-yellow-600">
                        {{ currentPage.w3c_validation?.summary?.warnings ?? 0 }}
                      </div>
                      <div class="text-gray-500">Warnings</div>
                    </div>
                    <div class="bg-blue-50 p-2 rounded">
                      <div class="font-bold text-blue-600">
                        {{ currentPage.w3c_validation?.summary?.info ?? 0 }}
                      </div>
                      <div class="text-gray-500">Info</div>
                    </div>
                  </div>
                  <div
                    v-if="
                      !currentPage.w3c_validation?.results ||
                      currentPage.w3c_validation.results.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No W3C validation issues found.
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'w3cIssuesSortColumn',
                              'w3cIssuesSortDirection',
                              'resource'
                            )
                          "
                        >
                          Resource
                          <ChevronUp
                            v-if="
                              w3cIssuesSortColumn === 'resource' &&
                              w3cIssuesSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              w3cIssuesSortColumn === 'resource' &&
                              w3cIssuesSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-12 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'w3cIssuesSortColumn',
                              'w3cIssuesSortDirection',
                              'line'
                            )
                          "
                        >
                          Line
                          <ChevronUp
                            v-if="
                              w3cIssuesSortColumn === 'line' &&
                              w3cIssuesSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              w3cIssuesSortColumn === 'line' &&
                              w3cIssuesSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-16 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'w3cIssuesSortColumn',
                              'w3cIssuesSortDirection',
                              'severity'
                            )
                          "
                        >
                          Severity
                          <ChevronUp
                            v-if="
                              w3cIssuesSortColumn === 'severity' &&
                              w3cIssuesSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              w3cIssuesSortColumn === 'severity' &&
                              w3cIssuesSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'w3cIssuesSortColumn',
                              'w3cIssuesSortDirection',
                              'message'
                            )
                          "
                        >
                          Message
                          <ChevronUp
                            v-if="
                              w3cIssuesSortColumn === 'message' &&
                              w3cIssuesSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              w3cIssuesSortColumn === 'message' &&
                              w3cIssuesSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedW3CIssues"
                        :key="'w3c-' + idx"
                        class="border-b"
                      >
                        <td
                          class="px-2 py-1 border text-gray-600 break-all max-w-40 cursor-help"
                          v-tippy="{
                            content: item.resource,
                            theme: 'diagnostic',
                            placement: 'top',
                          }"
                        >
                          {{ item.resource || "—" }}
                        </td>
                        <td
                          class="px-2 py-1 border text-center font-mono text-xs"
                          :class="
                            item.line !== null && item.line !== undefined
                              ? 'text-gray-700'
                              : 'text-gray-400'
                          "
                        >
                          {{ Number.isFinite(item.line) ? item.line : "—" }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.severity === 'Error'"
                            class="text-red-600 font-semibold"
                            >Error</span
                          >
                          <span
                            v-else-if="item.severity === 'Warning'"
                            class="text-yellow-600 font-semibold"
                            >Warning</span
                          >
                          <span v-else class="text-blue-500">Info</span>
                        </td>
                        <td class="px-2 py-1 border text-gray-700">
                          {{ item.message }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div
                    v-if="w3cTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.w3c--"
                      :disabled="sopPagination.w3c === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.w3c }} of {{ w3cTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.w3c++"
                      :disabled="sopPagination.w3c >= w3cTotalPages"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <!-- Additional SOP Compliance -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedAdditionalSop = !collapsedAdditionalSop"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedAdditionalSop }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Additional SOP Compliance
                    </h4>
                    <span class="text-sm font-bold text-gray-600"
                      >{{ currentPage.additional_sop?.score ?? 100 }}%</span
                    >
                    <span
                      v-if="
                        (currentPage.additional_sop?.status ?? 'Passed') ===
                        'Passed'
                      "
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" />
                      Passed</span
                    >
                    <span
                      v-else-if="
                        (currentPage.additional_sop?.status ?? '') === 'Warning'
                      "
                      class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-yellow-600" />
                      Warning</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><XCircle class="w-3 h-3 text-red-600" /> Failed</span
                    >
                  </div>
                </div>
                <div v-if="!collapsedAdditionalSop" class="mt-3">
                  <div class="grid grid-cols-3 gap-2 mb-3 text-xs text-center">
                    <div class="bg-green-50 p-2 rounded">
                      <div class="font-bold text-green-600">
                        {{ currentPage.additional_sop?.summary?.passed ?? 0 }}
                      </div>
                      <div class="text-gray-500">Passed</div>
                    </div>
                    <div class="bg-red-50 p-2 rounded">
                      <div class="font-bold text-red-600">
                        {{ currentPage.additional_sop?.summary?.failed ?? 0 }}
                      </div>
                      <div class="text-gray-500">Failed</div>
                    </div>
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-gray-700">
                        {{ currentPage.additional_sop?.summary?.total ?? 0 }}
                      </div>
                      <div class="text-gray-500">Total Checks</div>
                    </div>
                  </div>
                  <div
                    v-if="
                      !currentPage.additional_sop?.results ||
                      currentPage.additional_sop.results.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No additional SOP compliance checks were performed.
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'additionalSopSortColumn',
                              'additionalSopSortDirection',
                              'check'
                            )
                          "
                        >
                          Check
                          <ChevronUp
                            v-if="
                              additionalSopSortColumn === 'check' &&
                              additionalSopSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              additionalSopSortColumn === 'check' &&
                              additionalSopSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-16 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'additionalSopSortColumn',
                              'additionalSopSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              additionalSopSortColumn === 'status' &&
                              additionalSopSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              additionalSopSortColumn === 'status' &&
                              additionalSopSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'additionalSopSortColumn',
                              'additionalSopSortDirection',
                              'message'
                            )
                          "
                        >
                          Message
                          <ChevronUp
                            v-if="
                              additionalSopSortColumn === 'message' &&
                              additionalSopSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              additionalSopSortColumn === 'message' &&
                              additionalSopSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in sortedAdditionalSop"
                        :key="'sop-' + idx"
                        class="border-b"
                      >
                        <td
                          class="px-2 py-1 border text-gray-700 font-semibold"
                        >
                          {{ item.check }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Passed</span
                          >
                          <span
                            v-else-if="item.status === 'Warning'"
                            class="text-yellow-600 font-semibold inline-flex items-center gap-1"
                            ><AlertTriangle class="w-3 h-3 text-yellow-600" />
                            Warning</span
                          >
                          <span
                            v-else
                            class="text-red-600 font-semibold inline-flex items-center gap-1"
                            ><XCircle class="w-3 h-3 text-red-600" />
                            Failed</span
                          >
                        </td>
                        <td class="px-2 py-1 border text-gray-700">
                          {{ item.message }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- Broken Link Checker -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <div
                  class="flex items-center justify-between cursor-pointer select-none"
                  @click="collapsedBrokenLinks = !collapsedBrokenLinks"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-gray-400 transition-transform duration-200 text-xs inline-flex items-center"
                      :class="{ 'rotate-90': !collapsedBrokenLinks }"
                      ><ChevronRight class="w-3.5 h-3.5"
                    /></span>
                    <h4 class="text-lg font-bold text-gray-800">
                      Broken Link Checker
                    </h4>
                    <span class="text-sm font-bold text-gray-600"
                      >{{ currentPage.broken_links?.score ?? 100 }}%</span
                    >
                    <span
                      v-if="
                        (currentPage.broken_links?.status ?? 'Passed') ===
                        'Passed'
                      "
                      class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><CheckCircle2 class="w-3 h-3 text-green-600" /> All
                      Valid</span
                    >
                    <span
                      v-else-if="
                        (currentPage.broken_links?.status ?? '') === 'Warning'
                      "
                      class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><AlertTriangle class="w-3 h-3 text-yellow-600" />
                      Warning</span
                    >
                    <span
                      v-else
                      class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                      ><XCircle class="w-3 h-3 text-red-600" /> Broken</span
                    >
                  </div>
                </div>
                <div v-if="!collapsedBrokenLinks" class="mt-3">
                  <div class="grid grid-cols-3 gap-2 mb-3 text-xs text-center">
                    <div class="bg-gray-50 p-2 rounded">
                      <div class="font-bold text-gray-700">
                        {{ currentPage.broken_links?.summary?.total ?? 0 }}
                      </div>
                      <div class="text-gray-500">Total Links</div>
                    </div>
                    <div class="bg-green-50 p-2 rounded">
                      <div class="font-bold text-green-600">
                        {{ currentPage.broken_links?.summary?.passed ?? 0 }}
                      </div>
                      <div class="text-gray-500">Passed</div>
                    </div>
                    <div class="bg-red-50 p-2 rounded">
                      <div class="font-bold text-red-600">
                        {{ currentPage.broken_links?.summary?.failed ?? 0 }}
                      </div>
                      <div class="text-gray-500">Broken</div>
                    </div>
                  </div>
                  <div
                    v-if="
                      !currentPage.broken_links?.results ||
                      currentPage.broken_links.results.length === 0
                    "
                    class="text-xs text-gray-500"
                  >
                    No links to check on this page (max 50 checked).
                  </div>
                  <table v-else class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'brokenLinksSortColumn',
                              'brokenLinksSortDirection',
                              'url'
                            )
                          "
                        >
                          URL
                          <ChevronUp
                            v-if="
                              brokenLinksSortColumn === 'url' &&
                              brokenLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              brokenLinksSortColumn === 'url' &&
                              brokenLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-12 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'brokenLinksSortColumn',
                              'brokenLinksSortDirection',
                              'http'
                            )
                          "
                        >
                          HTTP
                          <ChevronUp
                            v-if="
                              brokenLinksSortColumn === 'http' &&
                              brokenLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              brokenLinksSortColumn === 'http' &&
                              brokenLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1 border w-16 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'brokenLinksSortColumn',
                              'brokenLinksSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              brokenLinksSortColumn === 'status' &&
                              brokenLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              brokenLinksSortColumn === 'status' &&
                              brokenLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1 border cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'brokenLinksSortColumn',
                              'brokenLinksSortDirection',
                              'message'
                            )
                          "
                        >
                          Message
                          <ChevronUp
                            v-if="
                              brokenLinksSortColumn === 'message' &&
                              brokenLinksSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              brokenLinksSortColumn === 'message' &&
                              brokenLinksSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in paginatedBrokenLinks"
                        :key="'bl-' + idx"
                        class="border-b"
                      >
                        <td
                          class="px-2 py-1 border text-gray-600 break-all text-xs"
                        >
                          {{ item.url.replace(/\\\//g, "/") }}
                        </td>
                        <td
                          class="px-2 py-1 border text-center font-mono text-xs"
                          :class="
                            item.http_status >= 200 && item.http_status < 400
                              ? 'text-green-600'
                              : 'text-red-600'
                          "
                        >
                          {{ item.http_status || "—" }}
                        </td>
                        <td class="px-2 py-1 border text-center">
                          <span
                            v-if="item.status === 'Passed'"
                            class="text-green-600 font-semibold inline-flex items-center gap-1"
                            ><CheckCircle2 class="w-3 h-3 text-green-600" />
                            Passed</span
                          >
                          <span
                            v-else
                            class="text-red-600 font-semibold inline-flex items-center gap-1"
                            ><XCircle class="w-3 h-3 text-red-600" />
                            Failed</span
                          >
                        </td>
                        <td class="px-2 py-1 border text-gray-700">
                          {{ item.message }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div
                    v-if="brokenLinksTotalPages > 1"
                    class="flex justify-between items-center mt-2 text-xs"
                  >
                    <button
                      @click="sopPagination.brokenLinks--"
                      :disabled="sopPagination.brokenLinks === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ sopPagination.brokenLinks }} of
                      {{ brokenLinksTotalPages }}</span
                    >
                    <button
                      @click="sopPagination.brokenLinks++"
                      :disabled="
                        sopPagination.brokenLinks >= brokenLinksTotalPages
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- WWW Redirect Consistency -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-base font-semibold mb-3">
                  WWW Redirect Consistency
                </h4>
                <div class="flex items-center gap-3 mb-3">
                  <span
                    :class="{
                      'text-green-600': wwwRedirectData?.is_consistent,
                      'text-red-500':
                        !wwwRedirectData?.is_consistent &&
                        wwwRedirectData?.status !== 'not_checked',
                      'text-gray-400':
                        wwwRedirectData?.status === 'not_checked',
                    }"
                    class="text-xl"
                  >
                    <CheckCircle2
                      v-if="wwwRedirectData?.is_consistent"
                      class="w-5 h-5 text-green-500"
                    />
                    <Clock
                      v-else-if="wwwRedirectData?.status === 'not_checked'"
                      class="w-5 h-5 text-amber-500"
                    />
                    <XCircle v-else class="w-5 h-5 text-red-500" />
                  </span>
                  <span class="text-sm font-medium text-gray-700">
                    {{
                      wwwRedirectData?.is_consistent
                        ? "Consistent redirect configured"
                        : wwwRedirectData?.status === "not_checked"
                        ? "Not checked yet"
                        : "Duplicate content risk detected"
                    }}
                  </span>
                </div>

                <div
                  v-if="wwwRedirectData?.status !== 'not_checked'"
                  class="text-xs text-gray-600 space-y-3"
                >
                  <!-- Redirect Chain Table -->
                  <table class="w-full border-collapse text-xs">
                    <thead>
                      <tr class="bg-gray-50 border-b">
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600"
                        >
                          Version
                        </th>
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600"
                        >
                          Initial URL
                        </th>
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600"
                        >
                          Final URL
                        </th>
                        <th
                          class="text-center px-2 py-1.5 font-semibold text-gray-600 w-12"
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Non-WWW row -->
                      <tr class="border-b">
                        <td class="px-2 py-1.5 font-medium">Non-WWW</td>
                        <td class="px-2 py-1.5 text-gray-500 break-all">
                          {{ wwwRedirectData?.requested_url || "—" }}
                        </td>
                        <td
                          class="px-2 py-1.5 break-all"
                          :class="{
                            'text-green-700':
                              wwwRedirectData?.requested_response?.final_url ===
                              wwwRedirectData?.requested_url,
                            'text-blue-600':
                              wwwRedirectData?.requested_response?.final_url !==
                              wwwRedirectData?.requested_url,
                          }"
                        >
                          {{
                            wwwRedirectData?.requested_response?.final_url ||
                            "—"
                          }}
                        </td>
                        <td class="px-2 py-1.5 text-center">
                          <span
                            :class="{
                              'text-green-600':
                                wwwRedirectData?.requested_response?.status >=
                                  200 &&
                                wwwRedirectData?.requested_response?.status <
                                  400,
                              'text-red-500':
                                wwwRedirectData?.requested_response?.status ===
                                  0 ||
                                wwwRedirectData?.requested_response?.status >=
                                  400,
                            }"
                            class="font-mono font-semibold"
                          >
                            {{
                              wwwRedirectData?.requested_response?.status || "?"
                            }}
                          </span>
                        </td>
                      </tr>
                      <!-- WWW row -->
                      <tr>
                        <td class="px-2 py-1.5 font-medium">WWW</td>
                        <td class="px-2 py-1.5 text-gray-500 break-all">
                          {{ wwwRedirectData?.counterpart_url || "—" }}
                        </td>
                        <td
                          class="px-2 py-1.5 break-all"
                          :class="{
                            'text-green-700':
                              wwwRedirectData?.counterpart_response
                                ?.final_url ===
                              wwwRedirectData?.counterpart_url,
                            'text-blue-600':
                              wwwRedirectData?.counterpart_response
                                ?.final_url !==
                              wwwRedirectData?.counterpart_url,
                          }"
                        >
                          {{
                            wwwRedirectData?.counterpart_response?.final_url ||
                            "—"
                          }}
                        </td>
                        <td class="px-2 py-1.5 text-center">
                          <span
                            :class="{
                              'text-green-600':
                                wwwRedirectData?.counterpart_response?.status >=
                                  200 &&
                                wwwRedirectData?.counterpart_response?.status <
                                  400,
                              'text-red-500':
                                wwwRedirectData?.counterpart_response
                                  ?.status === 0 ||
                                wwwRedirectData?.counterpart_response?.status >=
                                  400,
                            }"
                            class="font-mono font-semibold"
                          >
                            {{
                              wwwRedirectData?.counterpart_response?.status ||
                              "?"
                            }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div v-if="wwwRedirectData?.preferred_version !== 'unknown'">
                    <span class="font-semibold">Preferred version:</span>
                    <span class="ml-1 capitalize">{{
                      wwwRedirectData?.preferred_version
                    }}</span>
                  </div>

                  <!-- Redirect Chain Detail (shown only if there are hops) -->
                  <details
                    v-if="
                      (wwwRedirectData?.requested_response?.redirect_chain
                        ?.length || 0) > 1 ||
                      (wwwRedirectData?.counterpart_response?.redirect_chain
                        ?.length || 0) > 1
                    "
                    class="mt-2"
                  >
                    <summary
                      class="cursor-pointer text-blue-600 hover:text-blue-800 text-[11px]"
                    >
                      Show redirect chain details
                    </summary>
                    <div class="mt-2 space-y-2">
                      <div
                        v-if="
                          (wwwRedirectData?.requested_response?.redirect_chain
                            ?.length || 0) > 1
                        "
                      >
                        <span class="font-semibold text-[11px]"
                          >Non-WWW redirect hops:</span
                        >
                        <div class="flex items-center gap-1 flex-wrap mt-1">
                          <template
                            v-for="(hop, hIdx) in wwwRedirectData
                              ?.requested_response?.redirect_chain || []"
                            :key="'nw-' + hIdx"
                          >
                            <span
                              class="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-xxs break-all max-w-[200px]"
                              >{{ hop.url }}</span
                            >
                            <span
                              class="text-xxs font-semibold"
                              :class="{
                                'text-amber-600':
                                  hop.status >= 300 && hop.status < 400,
                                'text-green-600':
                                  hop.status >= 200 && hop.status < 300,
                                'text-red-500':
                                  hop.status === 0 || hop.status >= 400,
                              }"
                            >
                              [{{ hop.status || "?" }}]
                            </span>
                            <span
                              v-if="
                                hIdx <
                                (wwwRedirectData?.requested_response
                                  ?.redirect_chain?.length || 0) -
                                  1
                              "
                              class="text-gray-400 text-xxs"
                              >→</span
                            >
                          </template>
                        </div>
                      </div>
                      <div
                        v-if="
                          (wwwRedirectData?.counterpart_response?.redirect_chain
                            ?.length || 0) > 1
                        "
                      >
                        <span class="font-semibold text-[11px]"
                          >WWW redirect hops:</span
                        >
                        <div class="flex items-center gap-1 flex-wrap mt-1">
                          <template
                            v-for="(hop, hIdx) in wwwRedirectData
                              ?.counterpart_response?.redirect_chain || []"
                            :key="'ww-' + hIdx"
                          >
                            <span
                              class="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-xxs break-all max-w-[200px]"
                              >{{ hop.url }}</span
                            >
                            <span
                              class="text-xxs font-semibold"
                              :class="{
                                'text-amber-600':
                                  hop.status >= 300 && hop.status < 400,
                                'text-green-600':
                                  hop.status >= 200 && hop.status < 300,
                                'text-red-500':
                                  hop.status === 0 || hop.status >= 400,
                              }"
                            >
                              [{{ hop.status || "?" }}]
                            </span>
                            <span
                              v-if="
                                hIdx <
                                (wwwRedirectData?.counterpart_response
                                  ?.redirect_chain?.length || 0) -
                                  1
                              "
                              class="text-gray-400 text-xxs"
                              >→</span
                            >
                          </template>
                        </div>
                      </div>
                    </div>
                  </details>

                  <div
                    v-if="
                      !wwwRedirectData?.is_consistent &&
                      wwwRedirectData?.status === 'inconsistent'
                    "
                    class="p-2 bg-red-50 border border-red-200 rounded text-red-700"
                  >
                    <p class="font-semibold mb-1 flex items-center gap-1.5">
                      <AlertTriangle class="w-4 h-4 text-amber-600" /><span
                        >Duplicate Content Risk</span
                      >
                    </p>
                    <p>
                      Both www and non-www versions serve the same content (HTTP
                      200). Search engines may index both versions as separate
                      pages, diluting your SEO rankings.
                    </p>
                    <p class="mt-1">
                      <strong>Recommendation:</strong> Configure a permanent 301
                      redirect from one version to the other (e.g., non-www →
                      www) to consolidate page authority and prevent duplicate
                      content penalties.
                    </p>
                  </div>
                  <div
                    v-if="wwwRedirectData?.status === 'not_applicable'"
                    class="p-2 bg-gray-50 rounded text-gray-500"
                  >
                    <p>This check is not applicable to IP addresses.</p>
                  </div>
                </div>
              </div>

              <!-- HTTPS Redirect Check -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-base font-semibold mb-3">HTTPS Redirect</h4>
                <div class="flex items-center gap-3 mb-3">
                  <span
                    :class="{
                      'text-green-600': httpsRedirectData?.is_secure,
                      'text-red-500':
                        !httpsRedirectData?.is_secure &&
                        httpsRedirectData?.status !== 'not_checked' &&
                        httpsRedirectData?.status !== 'not_applicable',
                      'text-gray-400':
                        httpsRedirectData?.status === 'not_checked' ||
                        httpsRedirectData?.status === 'not_applicable',
                    }"
                    class="text-xl"
                  >
                    <CheckCircle2
                      v-if="httpsRedirectData?.is_secure"
                      class="w-5 h-5 text-green-500"
                    />
                    <Clock
                      v-else-if="
                        httpsRedirectData?.status === 'not_checked' ||
                        httpsRedirectData?.status === 'not_applicable'
                      "
                      class="w-5 h-5 text-amber-500"
                    />
                    <XCircle v-else class="w-5 h-5 text-red-500" />
                  </span>
                  <span class="text-sm font-medium text-gray-700">
                    {{
                      httpsRedirectData?.is_secure
                        ? "HTTP redirects to HTTPS"
                        : httpsRedirectData?.status === "not_applicable"
                        ? "Site is already HTTP"
                        : httpsRedirectData?.status === "not_checked"
                        ? "Not checked yet"
                        : "HTTP does not redirect to HTTPS"
                    }}
                  </span>
                </div>

                <div
                  v-if="
                    httpsRedirectData?.status !== 'not_checked' &&
                    httpsRedirectData?.status !== 'not_applicable'
                  "
                  class="text-xs text-gray-600 space-y-3"
                >
                  <!-- Redirect Chain Table -->
                  <table class="w-full border-collapse text-xs">
                    <thead>
                      <tr class="bg-gray-50 border-b">
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'httpsRedirectSortColumn',
                              'httpsRedirectSortDirection',
                              'version'
                            )
                          "
                        >
                          Version
                          <ChevronUp
                            v-if="
                              httpsRedirectSortColumn === 'version' &&
                              httpsRedirectSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              httpsRedirectSortColumn === 'version' &&
                              httpsRedirectSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'httpsRedirectSortColumn',
                              'httpsRedirectSortDirection',
                              'initialUrl'
                            )
                          "
                        >
                          Initial URL
                          <ChevronUp
                            v-if="
                              httpsRedirectSortColumn === 'initialUrl' &&
                              httpsRedirectSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              httpsRedirectSortColumn === 'initialUrl' &&
                              httpsRedirectSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-left px-2 py-1.5 font-semibold text-gray-600 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'httpsRedirectSortColumn',
                              'httpsRedirectSortDirection',
                              'finalUrl'
                            )
                          "
                        >
                          Final URL
                          <ChevronUp
                            v-if="
                              httpsRedirectSortColumn === 'finalUrl' &&
                              httpsRedirectSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              httpsRedirectSortColumn === 'finalUrl' &&
                              httpsRedirectSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                        <th
                          class="text-center px-2 py-1.5 font-semibold text-gray-600 w-12 cursor-pointer select-none hover:bg-blue-100 transition-colors"
                          @click="
                            toggleTableSort(
                              'httpsRedirectSortColumn',
                              'httpsRedirectSortDirection',
                              'status'
                            )
                          "
                        >
                          Status
                          <ChevronUp
                            v-if="
                              httpsRedirectSortColumn === 'status' &&
                              httpsRedirectSortDirection === 'asc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                          <ChevronDown
                            v-else-if="
                              httpsRedirectSortColumn === 'status' &&
                              httpsRedirectSortDirection === 'desc'
                            "
                            class="inline w-3 h-3 text-blue-600"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        class="border-b"
                        v-for="(row, idx) in sortedHttpsRedirectRows"
                        :key="'https-' + idx"
                      >
                        <td class="px-2 py-1.5 font-medium">
                          {{ row.version }}
                        </td>
                        <td class="px-2 py-1.5 text-gray-500 break-all">
                          {{ row.initialUrl }}
                        </td>
                        <td
                          class="px-2 py-1.5 break-all"
                          :class="{
                            'text-green-700': row.isSecure,
                            'text-red-600': !row.isSecure,
                          }"
                        >
                          {{ row.finalUrl }}
                        </td>
                        <td class="px-2 py-1.5 text-center">
                          <span
                            :class="{
                              'text-green-600':
                                row.statusCode >= 200 && row.statusCode < 400,
                              'text-red-500':
                                row.statusCode === 0 || row.statusCode >= 400,
                            }"
                            class="font-mono font-semibold"
                          >
                            {{ row.status }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Redirect Chain Detail -->
                  <details
                    v-if="
                      (httpsRedirectData?.http_response?.redirect_chain
                        ?.length || 0) > 1
                    "
                    class="mt-2"
                  >
                    <summary
                      class="cursor-pointer text-blue-600 hover:text-blue-800 text-[11px]"
                    >
                      Show redirect chain details
                    </summary>
                    <div class="mt-2">
                      <div>
                        <span class="font-semibold text-[11px]"
                          >HTTP redirect hops:</span
                        >
                        <div class="flex items-center gap-1 flex-wrap mt-1">
                          <template
                            v-for="(hop, hIdx) in httpsRedirectData
                              ?.http_response?.redirect_chain || []"
                            :key="'hs-' + hIdx"
                          >
                            <span
                              class="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-xxs break-all max-w-[200px]"
                              >{{ hop.url }}</span
                            >
                            <span
                              class="text-xxs font-semibold"
                              :class="{
                                'text-amber-600':
                                  hop.status >= 300 && hop.status < 400,
                                'text-green-600':
                                  hop.status >= 200 && hop.status < 300,
                                'text-red-500':
                                  hop.status === 0 || hop.status >= 400,
                              }"
                            >
                              [{{ hop.status || "?" }}]
                            </span>
                            <span
                              v-if="
                                hIdx <
                                (httpsRedirectData?.http_response
                                  ?.redirect_chain?.length || 0) -
                                  1
                              "
                              class="text-gray-400 text-xxs"
                              >→</span
                            >
                          </template>
                        </div>
                      </div>
                    </div>
                  </details>

                  <div
                    v-if="
                      !httpsRedirectData?.is_secure &&
                      httpsRedirectData?.status === 'insecure'
                    "
                    class="p-2 bg-red-50 border border-red-200 rounded text-red-700"
                  >
                    <p class="font-semibold mb-1 flex items-center gap-1.5">
                      <AlertTriangle class="w-4 h-4 text-amber-600" /><span
                        >Unencrypted Access Risk</span
                      >
                    </p>
                    <p>
                      The HTTP version of this page does not redirect to HTTPS.
                      Visitors and search engines can access an unencrypted
                      version, undermining trust and SEO rankings.
                    </p>
                    <p class="mt-1">
                      <strong>Recommendation:</strong> Configure your server to
                      permanently 301 redirect all HTTP traffic to HTTPS.
                    </p>
                  </div>
                </div>
              </div>
              <!-- Content Type Detection (Blog/News/Article) -->
              <div class="p-4 bg-white border rounded-md shadow-sm">
                <h4 class="text-base font-semibold mb-3">
                  Content Type Detection
                </h4>
                <div
                  v-if="blogNewsHasSection"
                  class="flex items-center gap-2 mb-2"
                >
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="{
                      'bg-green-100 text-green-700 border border-green-300':
                        blogNewsScore >= 70,
                      'bg-yellow-100 text-yellow-700 border border-yellow-300':
                        blogNewsScore >= 40 && blogNewsScore < 70,
                      'bg-red-100 text-red-700 border border-red-300':
                        blogNewsScore < 40,
                    }"
                  >
                    {{ blogNewsScore }}% confidence
                  </span>
                  <span class="text-sm text-green-700 font-medium">
                    <span class="inline-flex items-center gap-1.5"
                      ><CheckCircle2 class="w-4 h-4 text-green-600" />
                      Blog/News/Article section detected</span
                    >
                  </span>
                </div>
                <div v-else class="flex items-center gap-2 mb-2">
                  <span class="text-sm text-gray-500">
                    <span class="inline-flex items-center gap-1.5"
                      ><XCircle class="w-4 h-4 text-gray-500" /> No
                      blog/news/article section detected</span
                    >
                  </span>
                </div>

                <!-- Detected Types -->
                <div v-if="blogNewsTypes.length" class="mb-2">
                  <span class="text-xs text-gray-500 mr-1">Classified as:</span>
                  <span
                    v-for="type in blogNewsTypes"
                    :key="type"
                    class="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs mr-1"
                  >
                    {{ type }}
                  </span>
                </div>

                <!-- Signal Details (expandable) -->
                <details
                  v-if="blogNewsSignals"
                  class="mt-2 text-xs text-gray-600"
                >
                  <summary
                    class="cursor-pointer hover:text-gray-800 font-medium"
                  >
                    View detection signals
                  </summary>
                  <div class="mt-2 space-y-1 pl-2 border-l-2 border-gray-200">
                    <div
                      v-for="(signal, key) in blogNewsSignals"
                      :key="key"
                      class="flex items-center justify-between py-0.5"
                    >
                      <span
                        :class="{
                          'text-green-600': signal.detected,
                          'text-gray-400': !signal.detected,
                        }"
                      >
                        <span class="inline-flex items-center gap-1"
                          ><CheckCircle2
                            v-if="signal.detected"
                            class="w-3.5 h-3.5 text-green-600"
                          /><span v-else class="inline-block w-3.5 text-center"
                            >—</span
                          ><span>{{ signal.label }}</span></span
                        >
                      </span>
                      <span class="text-gray-400">
                        {{ signal.earned }}/{{ signal.weight }}
                      </span>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
          <!-- Social Media Tab -->
          <div v-else-if="activeTab === 'Social Media'">
            <div class="social-media bg-gray-50 p-4 rounded-md border">
              <h3 class="text-base font-semibold mb-2">
                Social Media Presence
              </h3>

              <!-- OpenGraph Tags -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Facebook OpenGraph Tags</p>
                <div
                  v-if="currentPage.social?.has_og_tags"
                  class="mt-2 space-y-1 text-xs"
                >
                  <p v-if="currentPage.social.og_title">
                    <strong>OG Title:</strong> {{ currentPage.social.og_title }}
                  </p>
                  <p v-if="currentPage.social.og_description">
                    <strong>OG Description:</strong>
                    {{ currentPage.social.og_description }}
                  </p>
                  <p v-if="currentPage.social.og_image">
                    <strong>OG Image:</strong>
                    <span class="text-blue-600 break-all">{{
                      currentPage.social.og_image
                    }}</span>
                  </p>
                  <p v-if="currentPage.social.og_url">
                    <strong>OG URL:</strong> {{ currentPage.social.og_url }}
                  </p>
                  <p v-if="currentPage.social.og_type">
                    <strong>OG Type:</strong> {{ currentPage.social.og_type }}
                  </p>
                  <p v-if="currentPage.social.og_site_name">
                    <strong>OG Site Name:</strong>
                    {{ currentPage.social.og_site_name }}
                  </p>
                  <p class="text-green-500 font-bold mt-1">
                    <span class="inline-flex items-center gap-1.5"
                      ><CheckCircle2 class="w-4 h-4 text-green-500" /> OpenGraph
                      tags are present</span
                    >
                  </p>
                </div>
                <p v-else class="text-red-500 font-bold mt-2">
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No OpenGraph tags detected. OG tags control how your page
                  appears when shared on Facebook, LinkedIn, and other
                  platforms.
                </p>
              </div>

              <!-- Twitter Card Tags -->
              <div class="p-3 border-b bg-white shadow-md rounded-md mb-2">
                <p class="font-bold">Twitter Card Markup</p>
                <div
                  v-if="currentPage.social?.has_twitter_card"
                  class="mt-2 space-y-1 text-xs"
                >
                  <p v-if="currentPage.social.twitter_card_type">
                    <strong>Card Type:</strong>
                    {{ currentPage.social.twitter_card_type }}
                  </p>
                  <p v-if="currentPage.social.twitter_title">
                    <strong>Twitter Title:</strong>
                    {{ currentPage.social.twitter_title }}
                  </p>
                  <p v-if="currentPage.social.twitter_description">
                    <strong>Twitter Description:</strong>
                    {{ currentPage.social.twitter_description }}
                  </p>
                  <p v-if="currentPage.social.twitter_image">
                    <strong>Twitter Image:</strong>
                    <span class="text-blue-600 break-all">{{
                      currentPage.social.twitter_image
                    }}</span>
                  </p>
                  <p class="text-green-500 font-bold mt-1">
                    <span class="inline-flex items-center gap-1.5"
                      ><CheckCircle2 class="w-4 h-4 text-green-500" /> Twitter
                      Card markup is present</span
                    >
                  </p>
                </div>
                <p v-else class="text-red-500 font-bold mt-2">
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No Twitter Card markup detected. Twitter Cards control how
                  your page appears when shared on Twitter/X.
                </p>
              </div>

              <!-- Social Media Links -->
              <div class="p-3 bg-white shadow-md rounded-md">
                <p class="font-bold">Social Media Platform Links</p>
                <div v-if="currentPage.social?.has_social_links" class="mt-2">
                  <p class="text-xs text-gray-600 mb-1">
                    Found on the following platforms:
                    <span class="font-semibold capitalize">{{
                      currentPage.social.found_platforms?.join(", ")
                    }}</span>
                  </p>
                  <ul
                    class="list-disc list-inside text-xs text-blue-600 space-y-1"
                  >
                    <li
                      v-for="(link, idx) in currentPage.social.social_links"
                      :key="idx"
                      class="break-all"
                    >
                      {{ link }}
                    </li>
                  </ul>
                  <p class="text-green-500 font-bold mt-1">
                    <span class="inline-flex items-center gap-1.5"
                      ><CheckCircle2 class="w-4 h-4 text-green-500" /> Social
                      media platform links found</span
                    >
                  </p>
                </div>
                <p v-else class="text-yellow-500 font-bold mt-2">
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  No social media platform links found on this page. Consider
                  adding links to your social profiles.
                </p>
              </div>
            </div>
          </div>

          <!-- Schema Markup Tab -->
          <div v-else-if="activeTab === 'Schema Markup'">
            <div class="schema-markup bg-gray-50 p-4 rounded-md border">
              <h3 class="text-base font-semibold mb-3">
                Schema.org Structured Data
              </h3>

              <!-- Overall Score -->
              <div class="bg-white shadow-md rounded-md p-4 mb-4">
                <div class="flex items-center justify-between mb-2">
                  <p class="font-bold text-sm">Schema Markup Quality Score</p>
                  <span
                    class="text-sm font-bold px-2 py-1 rounded"
                    :class="{
                      'bg-green-100 text-green-700': schemaScore >= 70,
                      'bg-yellow-100 text-yellow-700':
                        schemaScore >= 50 && schemaScore < 70,
                      'bg-red-100 text-red-700': schemaScore < 50,
                    }"
                  >
                    {{ schemaScore }}/100
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div
                    class="h-3 rounded-full transition-all duration-500"
                    :class="{
                      'bg-green-500': schemaScore >= 70,
                      'bg-yellow-400': schemaScore >= 50 && schemaScore < 70,
                      'bg-red-500': schemaScore < 50,
                    }"
                    :style="{ width: schemaScore + '%' }"
                  ></div>
                </div>
                <p class="text-xs text-gray-500 mt-2">
                  <span
                    v-if="schemaHasMarkup"
                    class="inline-flex items-center gap-1.5 text-green-600"
                    ><CheckCircle2 class="w-3.5 h-3.5" /> Schema markup
                    detected</span
                  >
                  <span
                    v-else
                    class="text-red-500 inline-flex items-center gap-1.5"
                    ><XCircle class="w-3.5 h-3.5" /> No schema markup
                    detected</span
                  >
                </p>

                <!-- Score Breakdown -->
                <div
                  v-if="
                    schemaHasMarkup &&
                    schemaScoreBreakdown &&
                    Object.keys(schemaScoreBreakdown).length
                  "
                  class="mt-3 pt-3 border-t border-gray-100"
                >
                  <p class="text-xs font-semibold text-gray-500 mb-2">
                    SCORE BREAKDOWN
                  </p>
                  <div
                    v-for="(item, key) in schemaScoreBreakdown"
                    :key="key"
                    class="flex items-center justify-between text-xs mb-1"
                  >
                    <span class="text-gray-600">{{ item.label }}</span>
                    <span
                      v-if="item.applicable === false"
                      class="font-semibold ml-2 text-gray-400"
                      title="Not applicable — the tool cannot determine whether this criterion is relevant for this site."
                    >
                      N/A
                    </span>
                    <span
                      v-else
                      class="font-semibold ml-2"
                      :class="{
                        'text-green-600': item.earned >= item.points * 0.7,
                        'text-yellow-600':
                          item.earned >= item.points * 0.3 &&
                          item.earned < item.points * 0.7,
                        'text-red-600': item.earned < item.points * 0.3,
                      }"
                    >
                      {{ item.earned }}/{{ item.points }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-400 mt-2">
                    Final score is normalised using only applicable criteria.
                    <span v-if="schemaNaCount > 0">
                      {{ schemaNaCount }} criterion was skipped because the tool
                      could not verify it.
                    </span>
                  </p>
                </div>
              </div>

              <!-- Summary Stats -->
              <div
                v-if="schemaHasMarkup"
                class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4"
              >
                <div class="bg-white shadow-md rounded-md p-3 text-center">
                  <p class="text-xs text-gray-500">Format</p>
                  <p class="text-sm font-bold">{{ schemaFormatLabel }}</p>
                </div>
                <div class="bg-white shadow-md rounded-md p-3 text-center">
                  <p class="text-xs text-gray-500">JSON-LD Blocks</p>
                  <p class="text-sm font-bold">
                    {{ schemaData.jsonld_blocks_count || 0 }}
                  </p>
                </div>
                <div class="bg-white shadow-md rounded-md p-3 text-center">
                  <p class="text-xs text-gray-500">Microdata Items</p>
                  <p class="text-sm font-bold">
                    {{ schemaData.microdata_items_count || 0 }}
                  </p>
                </div>
              </div>

              <!-- Detected Schema Types (ALL types found on the page) -->
              <div
                v-if="schemaHasMarkup"
                class="bg-white shadow-md rounded-md p-4 mb-4"
              >
                <p class="font-bold text-sm mb-1">Detected Schema Types</p>
                <p class="text-xs text-gray-500 mb-3">
                  All schema.org @type values found on this page via JSON-LD
                  and/or Microdata — including supporting types like WebPage and
                  ListItem.
                </p>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="type in schemaAllDetectedTypes"
                    :key="type"
                    class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="{
                      'bg-green-100 text-green-700 border border-green-300':
                        schemaAllDetected[type]?.via === 'jsonld',
                      'bg-blue-100 text-blue-700 border border-blue-300':
                        schemaAllDetected[type]?.via === 'microdata',
                      'bg-purple-100 text-purple-700 border border-purple-300':
                        schemaAllDetected[type]?.via &&
                        schemaAllDetected[type].via !== 'jsonld' &&
                        schemaAllDetected[type].via !== 'microdata',
                    }"
                  >
                    {{ type }}
                    <span class="ml-1 text-gray-400 font-normal">
                      ({{ schemaAllDetected[type]?.via }})
                    </span>
                  </span>
                </div>
                <p
                  v-if="!schemaAllDetectedTypes.length"
                  class="text-xs text-gray-400"
                >
                  No types were detected.
                </p>
              </div>

              <!-- Recommended Schema Types (the 12-type checklist) -->
              <div class="bg-white shadow-md rounded-md p-4 mb-4">
                <p class="font-bold text-sm mb-1">Recommended Schema Types</p>
                <p class="text-xs text-gray-500 mb-3">
                  These are commonly used schema types that can enable rich
                  results. Not every type is needed — only implement those
                  relevant to your site's content and purpose.
                </p>
                <div
                  class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                >
                  <div
                    v-for="type in schemaTargetTypes"
                    :key="type"
                    class="border rounded-md p-2 text-center text-xs"
                    :class="{
                      'border-green-400 bg-green-50':
                        schemaRecommendedDetails[type]?.detected,
                      'border-gray-200 bg-gray-50':
                        !schemaRecommendedDetails[type]?.detected,
                    }"
                  >
                    <p
                      class="font-semibold"
                      :class="{
                        'text-green-700':
                          schemaRecommendedDetails[type]?.detected,
                        'text-gray-400':
                          !schemaRecommendedDetails[type]?.detected,
                      }"
                    >
                      <CheckCircle2
                        v-if="schemaRecommendedDetails[type]?.detected"
                        class="w-3.5 h-3.5 text-green-600 inline-block mr-1 align-middle"
                      />
                      <span v-else class="inline-block w-3.5 text-center mr-1"
                        >—</span
                      >
                      {{ type }}
                    </p>
                    <p
                      v-if="
                        schemaRecommendedDetails[type]?.detected &&
                        schemaRecommendedDetails[type]?.via
                      "
                      class="text-gray-400 mt-0.5"
                    >
                      via {{ schemaRecommendedDetails[type].via }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Missing Recommended Properties -->
              <div
                v-if="
                  schemaHasMarkup &&
                  schemaMissingRec &&
                  Object.keys(schemaMissingRec).length
                "
                class="bg-white shadow-md rounded-md p-4 mb-4"
              >
                <p class="font-bold text-sm mb-2 text-yellow-600">
                  <AlertTriangle
                    class="w-4 h-4 inline-block mr-1 align-text-bottom"
                  />
                  Missing Recommended Properties
                </p>
                <p class="text-xs text-gray-500 mb-2">
                  These detected schema types are missing properties that Google
                  recommends for eligibility in enhanced search result features.
                </p>
                <div
                  v-for="(props, type) in schemaMissingRec"
                  :key="type"
                  class="text-xs mb-1"
                >
                  <span class="font-semibold">{{ type }}:</span>
                  <span class="text-yellow-700">
                    add {{ props.join(", ") }}
                  </span>
                </div>
              </div>

              <!-- Raw JSON-LD Viewer -->
              <div
                v-if="schemaData.jsonld_raw"
                class="bg-white shadow-md rounded-md p-4"
              >
                <div
                  class="flex items-center justify-between cursor-pointer"
                  @click="schemaShowRaw = !schemaShowRaw"
                >
                  <p class="font-bold text-sm">Raw JSON-LD</p>
                  <span class="text-xs text-blue-500">
                    <span class="inline-flex items-center gap-1"
                      ><ChevronUp
                        v-if="schemaShowRaw"
                        class="w-3.5 h-3.5"
                      /><ChevronDown v-else class="w-3.5 h-3.5" /><span>{{
                        schemaShowRaw ? "Hide" : "Show"
                      }}</span></span
                    >
                  </span>
                </div>
                <pre
                  v-if="schemaShowRaw"
                  class="bg-gray-100 p-3 rounded text-xs overflow-auto mt-2 max-h-96"
                  >{{ schemaData.jsonld_raw }}</pre
                >
              </div>

              <!-- No schema detected message -->
              <div
                v-if="!schemaHasMarkup"
                class="bg-red-50 border border-red-200 rounded-md p-4 text-center"
              >
                <p class="text-red-600 font-semibold mb-1">
                  <span class="inline-flex items-center gap-1.5"
                    ><XCircle class="w-4 h-4 text-red-500" /> No structured data
                    detected</span
                  >
                </p>
                <p class="text-xs text-gray-600">
                  This page does not have any schema.org structured data
                  (JSON-LD or Microdata). Adding schema markup helps search
                  engines understand your content and can enable rich results
                  like star ratings, FAQs, breadcrumbs, and more in search
                  results. We recommend starting with Organization or
                  LocalBusiness schema.
                </p>
                <a
                  href="https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data"
                  target="_blank"
                  class="text-blue-500 text-xs underline mt-1 inline-block"
                >
                  Learn more about structured data →
                </a>
              </div>
            </div>
          </div>

          <!-- Resource Audit Tab -->
          <div
            v-else-if="activeTab === 'Resource Audit'"
            class="resource-audit-content"
          >
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading audit results...</p>
              </div>
            </div>
            <div v-else>
              <!-- Summary Cards -->
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div
                  v-for="tab in auditTabs"
                  :key="tab.id"
                  class="p-4 rounded-lg border text-center transition-all hover:shadow-md cursor-pointer"
                  :class="
                    activeAuditTab === tab.id
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-white border-gray-100'
                  "
                  @click="activeAuditTab = tab.id"
                >
                  <span
                    class="block text-2xl font-bold"
                    :class="
                      activeAuditTab === tab.id
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    "
                  >
                    {{ tab.count }}
                  </span>
                  <span
                    class="text-xs font-medium uppercase tracking-wider text-gray-500"
                    >{{ tab.name }}</span
                  >
                </div>
              </div>

              <!-- CSS Results -->
              <div v-if="activeAuditTab === 'css'" class="animate-fadeIn">
                <div
                  v-if="auditResults.results.css.length === 0"
                  class="text-center py-6 text-gray-400"
                >
                  <span class="inline-flex items-center gap-1.5"
                    ><CheckCircle2 class="w-4 h-4 text-green-500" /> No unused
                    CSS files found.</span
                  >
                </div>
                <div v-else>
                  <div class="grid grid-cols-1 gap-2 mb-4">
                    <div
                      v-for="(item, index) in paginatedCss"
                      :key="index"
                      class="p-3 border rounded bg-white text-xs"
                    >
                      <!-- New format: file-level -->
                      <div
                        v-if="item.file"
                        class="flex justify-between items-center"
                      >
                        <div class="min-w-0 flex-1">
                          <span class="font-mono text-pink-600 font-bold">{{
                            item.file
                          }}</span>
                          <span
                            class="text-gray-400 text-xxs ml-2 break-all"
                            v-if="item.url"
                          >
                            {{ item.url }}
                          </span>
                          <span
                            class="text-gray-400 text-xxs ml-2 break-all"
                            v-else-if="item.path"
                          >
                            {{ item.path }}
                          </span>
                        </div>
                        <span
                          class="text-red-500 font-semibold text-[11px] whitespace-nowrap ml-3"
                        >
                          All {{ item.total_count }} selectors unused
                        </span>
                      </div>
                      <!-- Old format fallback: selector-level -->
                      <div v-else class="flex justify-between">
                        <span class="font-mono text-pink-600 font-bold">{{
                          item.selector || "(unknown)"
                        }}</span>
                        <span class="text-gray-400 text-xxs">{{
                          (item.files || []).join(", ")
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex justify-between items-center mt-4 text-xs">
                    <button
                      @click="auditPagination.css--"
                      :disabled="auditPagination.css === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ auditPagination.css }} of
                      {{
                        Math.ceil(
                          auditResults.summary.unused_css_count /
                            auditItemsPerPage
                        )
                      }}</span
                    >
                    <button
                      @click="auditPagination.css++"
                      :disabled="
                        auditPagination.css >=
                        Math.ceil(
                          auditResults.summary.unused_css_count /
                            auditItemsPerPage
                        )
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- JS Results -->
              <div v-if="activeAuditTab === 'js'">
                <div
                  v-if="auditResults.results.js_functions.length === 0"
                  class="text-center py-6 text-gray-400"
                >
                  <span class="inline-flex items-center gap-1.5"
                    ><CheckCircle2 class="w-4 h-4 text-green-500" /> No unused
                    JS files found.</span
                  >
                </div>
                <div v-else>
                  <div class="grid grid-cols-1 gap-2 mb-4">
                    <div
                      v-for="(item, index) in paginatedJs"
                      :key="index"
                      class="p-3 border rounded bg-white text-xs"
                    >
                      <!-- New format: file-level -->
                      <div
                        v-if="item.file"
                        class="flex justify-between items-center"
                      >
                        <div class="min-w-0 flex-1">
                          <span class="font-mono text-pink-600 font-bold">
                            {{ item.file }}
                          </span>
                          <span
                            class="text-gray-400 text-xxs ml-2 break-all"
                            v-if="item.url"
                          >
                            {{ item.url }}
                          </span>
                          <span
                            class="text-gray-400 text-xxs ml-2 break-all"
                            v-else-if="item.path"
                          >
                            {{ item.path }}
                          </span>
                        </div>
                        <span
                          class="text-red-500 font-semibold text-[11px] whitespace-nowrap ml-3"
                        >
                          All {{ item.total_count }} functions unused
                        </span>
                      </div>
                      <!-- Old format fallback: function-level -->
                      <div v-else class="flex justify-between">
                        <span class="font-mono text-pink-600 font-bold">
                          {{ item.function || "(unknown)" }}()
                        </span>
                        <span class="text-gray-400 text-xxs">
                          {{ item.file || item.path || "" }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-between items-center mt-4 text-xs">
                    <button
                      @click="auditPagination.js--"
                      :disabled="auditPagination.js === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ auditPagination.js }} of
                      {{
                        Math.ceil(
                          auditResults.summary.unused_js_functions_count /
                            auditItemsPerPage
                        )
                      }}</span
                    >
                    <button
                      @click="auditPagination.js++"
                      :disabled="
                        auditPagination.js >=
                        Math.ceil(
                          auditResults.summary.unused_js_functions_count /
                            auditItemsPerPage
                        )
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- Images Results -->
              <div v-if="activeAuditTab === 'images'">
                <div
                  v-if="auditResults.results.images.length === 0"
                  class="text-center py-6 text-gray-400"
                >
                  <span class="inline-flex items-center gap-1.5"
                    ><CheckCircle2 class="w-4 h-4 text-green-500" /> No unused
                    images found.</span
                  >
                </div>
                <div v-else>
                  <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div
                      v-for="(item, index) in paginatedImages"
                      :key="index"
                      class="p-2 border rounded bg-white text-center hover:shadow-sm"
                    >
                      <div
                        class="h-16 bg-gray-50 flex items-center justify-center rounded mb-2 text-gray-400"
                      >
                        <ImageIcon class="w-6 h-6" />
                      </div>
                      <span
                        class="text-xxs font-medium text-gray-600 truncate block cursor-help"
                        v-tippy="{
                          content: item.image,
                          theme: 'diagnostic',
                          placement: 'top',
                        }"
                        >{{ item.image }}</span
                      >
                    </div>
                  </div>
                  <div class="flex justify-between items-center mt-4 text-xs">
                    <button
                      @click="auditPagination.images--"
                      :disabled="auditPagination.images === 1"
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span
                      >Page {{ auditPagination.images }} of
                      {{
                        Math.ceil(
                          auditResults.summary.unused_images_count /
                            auditItemsPerPage
                        )
                      }}</span
                    >
                    <button
                      @click="auditPagination.images++"
                      :disabled="
                        auditPagination.images >=
                        Math.ceil(
                          auditResults.summary.unused_images_count /
                            auditItemsPerPage
                        )
                      "
                      class="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Redundant Resources Tab -->
          <div
            v-else-if="activeTab === 'Redundant Resources'"
            class="resource-audit-content"
          >
            <RedundantResources :auditResults="auditResults" />
          </div>

          <!-- Grammar and Spelling Tab -->
          <div
            v-else-if="activeTab === 'Grammar/Spelling'"
            class="resource-audit-content"
          >
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading grammar and spelling results...</p>
              </div>
            </div>
            <div v-else>
              <div class="mb-4 p-4 rounded-lg border bg-white">
                <span class="block text-2xl font-bold text-gray-700">
                  {{ grammarSpellingIssues.length }}
                </span>
                <span
                  class="text-xs font-medium uppercase tracking-wider text-gray-500"
                  >Grammar/Spelling Issues</span
                >
              </div>

              <div
                v-if="auditResults.results.grammar_spelling?.error"
                class="p-4 border border-yellow-200 bg-yellow-50 text-yellow-700 rounded text-sm"
              >
                {{ auditResults.results.grammar_spelling.error }}
              </div>
              <div
                v-else-if="grammarSpellingIssues.length === 0"
                class="text-center py-6 text-gray-400"
              >
                No grammar or spelling issues found.
              </div>
              <div v-else>
                <div class="grid grid-cols-1 gap-3 mb-4">
                  <div
                    v-for="(item, index) in paginatedGrammar"
                    :key="index"
                    class="p-3 border rounded bg-white text-xs"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <div class="font-semibold text-gray-700 capitalize">
                          {{ item.type }} issue
                        </div>
                        <div class="mt-1 text-red-600 break-words">
                          {{ item.text }}
                        </div>
                        <div class="mt-1 text-green-700 break-words">
                          Suggestion: {{ item.suggestion }}
                        </div>
                        <div
                          v-if="item.explanation"
                          class="mt-1 text-gray-500 break-words"
                        >
                          {{ item.explanation }}
                        </div>
                      </div>
                      <span
                        class="shrink-0 px-2 py-1 rounded text-xxs font-semibold uppercase"
                        :class="{
                          'bg-red-100 text-red-700': item.severity === 'high',
                          'bg-yellow-100 text-yellow-700':
                            item.severity === 'medium',
                          'bg-gray-100 text-gray-600': item.severity === 'low',
                        }"
                      >
                        {{ item.severity }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="flex justify-between items-center mt-4 text-xs">
                  <button
                    @click="auditPagination.grammar--"
                    :disabled="auditPagination.grammar === 1"
                    class="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span
                    >Page {{ auditPagination.grammar }} of
                    {{
                      Math.ceil(
                        grammarSpellingIssues.length / auditItemsPerPage
                      )
                    }}</span
                  >
                  <button
                    @click="auditPagination.grammar++"
                    :disabled="
                      auditPagination.grammar >=
                      Math.ceil(
                        grammarSpellingIssues.length / auditItemsPerPage
                      )
                    "
                    class="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Dropdowns Tab -->
          <div
            v-else-if="activeTab === 'Navigation Dropdowns'"
            class="resource-audit-content"
          >
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading navigation dropdown results...</p>
              </div>
            </div>
            <div
              v-else-if="!navigationDropdownReport"
              class="text-center py-6 text-gray-400"
            >
              — No navigation dropdown report available.
            </div>
            <div
              v-else-if="navigationDropdownReport.summary?.dropdownsFound === 0"
              class="text-center py-6 text-gray-400"
            >
              <span class="inline-flex items-center gap-1.5"
                ><CheckCircle2 class="w-4 h-4 text-green-500" /> No dropdown
                menus detected on this page.</span
              >
            </div>
            <div v-else class="space-y-4">
              <!-- Score Card -->
              <div class="p-4 rounded-lg border bg-white">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <div class="text-2xl font-bold text-gray-700">
                      {{ navigationDropdownReport.score }}%
                    </div>
                    <div
                      class="text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      Navigation Dropdown Check
                    </div>
                  </div>
                  <span
                    class="px-3 py-1 rounded-full text-xs font-semibold"
                    :class="{
                      'bg-green-100 text-green-700':
                        navigationDropdownReport.status === 'Passed',
                      'bg-red-100 text-red-700':
                        navigationDropdownReport.status === 'Failed',
                    }"
                  >
                    {{ navigationDropdownReport.status }}
                  </span>
                </div>
                <div class="mt-3 text-xs text-gray-600">
                  Found
                  {{ navigationDropdownReport.summary?.dropdownsFound || 0 }}
                  dropdowns &middot;
                  {{ navigationDropdownReport.summary?.passed || 0 }} passed
                  &middot;
                  {{ navigationDropdownReport.summary?.failed || 0 }} failed
                </div>
              </div>

              <!-- Individual Results -->
              <div
                v-for="(item, index) in navigationDropdownReport.results || []"
                :key="index"
                class="p-3 border rounded bg-white text-xs"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="font-semibold text-gray-700">
                      {{ item.menuName }}
                    </div>
                    <div class="mt-1 text-gray-500">{{ item.message }}</div>
                    <div
                      class="mt-2 text-gray-700 cursor-help"
                      v-tippy="{
                        content: item.recommendation,
                        theme: 'diagnostic',
                        placement: 'top',
                      }"
                    >
                      <strong>Recommendation:</strong> {{ item.recommendation }}
                    </div>
                  </div>
                  <span
                    class="shrink-0 px-2 py-1 rounded text-xxs font-semibold uppercase"
                    :class="{
                      'bg-green-100 text-green-700': item.status === 'Passed',
                      'bg-red-100 text-red-700': item.status === 'Failed',
                    }"
                  >
                    {{ item.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- XSS Risks Tab -->
          <div
            v-else-if="activeTab === 'XSS Risks'"
            class="resource-audit-content"
          >
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading XSS scan results...</p>
              </div>
            </div>
            <XssRiskPanel
              v-else
              :xss-data="auditResults.results?.xss_risks || null"
            />
          </div>

          <!-- SQL Injection Tab -->
          <div
            v-else-if="activeTab === 'SQL Injection'"
            class="resource-audit-content"
          >
            <!-- ── Deep Scan Controls ──────────────────────────────────── -->
            <div
              class="mb-6 p-5 rounded-lg border bg-gradient-to-r from-blue-50 to-indigo-50"
            >
              <div
                class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              >
                <div>
                  <h3
                    class="text-base font-bold text-gray-800 flex items-center gap-2"
                  >
                    <Search class="w-4 h-4 text-blue-600" /> Live SQL Injection
                    Scanner
                    <span
                      v-if="sqliLiveResults"
                      class="text-xs px-2 py-0.5 rounded font-semibold"
                      :class="{
                        'bg-green-100 text-green-700':
                          sqliLiveResults.risk_level === 'None',
                        'bg-yellow-100 text-yellow-700':
                          sqliLiveResults.risk_level === 'Low',
                        'bg-orange-100 text-orange-700':
                          sqliLiveResults.risk_level === 'Medium',
                        'bg-red-100 text-red-700': [
                          'High',
                          'Critical',
                        ].includes(sqliLiveResults.risk_level),
                        'bg-gray-100 text-gray-700':
                          sqliLiveResults.risk_level === 'Error',
                      }"
                    >
                      {{ sqliLiveResults.risk_level }} Risk
                    </span>
                  </h3>
                  <p class="text-xs text-gray-500 mt-1">
                    Uses headless browser automation to test forms for SQL
                    injection vulnerabilities.
                  </p>
                </div>
                <button
                  @click="runSqliLiveScan"
                  :disabled="resultsStillProcessing"
                  v-tippy="{
                    content:
                      resultsStillProcessing && !sqliScanning
                        ? 'Certain Results are still processing'
                        : '',
                    theme: 'diagnostic',
                    placement: 'top',
                  }"
                  class="px-5 py-2.5 rounded-lg text-white font-semibold text-sm transition flex items-center gap-2 shrink-0"
                  :class="
                    resultsStillProcessing
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  "
                >
                  <span
                    v-if="sqliScanning"
                    class="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"
                  ></span>
                  {{
                    sqliScanning
                      ? "Scanning..."
                      : sqliLiveResults
                      ? "Re-Scan"
                      : "Start Deep Scan"
                  }}
                </button>
              </div>

              <p
                v-if="sqliScanError"
                class="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded"
              >
                {{ sqliScanError }}
              </p>
            </div>

            <!-- ── Live Scan Results ───────────────────────────────────── -->
            <div v-if="sqliLiveResults && sqliLiveResults.status !== 'ERROR'">
              <!-- Risk Score Gauge -->
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div class="p-4 rounded-lg border bg-white text-center">
                  <div
                    class="text-3xl font-extrabold"
                    :class="{
                      'text-green-600': sqliLiveResults.risk_score <= 10,
                      'text-yellow-500':
                        sqliLiveResults.risk_score > 10 &&
                        sqliLiveResults.risk_score <= 30,
                      'text-orange-500':
                        sqliLiveResults.risk_score > 30 &&
                        sqliLiveResults.risk_score <= 60,
                      'text-red-600': sqliLiveResults.risk_score > 60,
                    }"
                  >
                    {{ sqliLiveResults.risk_score }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">Risk Score</div>
                </div>
                <div class="p-4 rounded-lg border bg-white text-center">
                  <div class="text-3xl font-extrabold text-gray-700">
                    {{ sqliLiveResults.forms_found || 0 }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">Forms Found</div>
                </div>
                <div class="p-4 rounded-lg border bg-white text-center">
                  <div class="text-3xl font-extrabold text-blue-600">
                    {{ sqliLiveResults.summary?.forms_tested || 0 }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">Forms Tested</div>
                </div>
                <div class="p-4 rounded-lg border bg-white text-center">
                  <div class="text-3xl font-extrabold text-gray-400">
                    {{ sqliLiveResults.summary?.forms_skipped || 0 }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    Skipped (Protected)
                  </div>
                </div>
              </div>

              <!-- Vulnerability Summary -->
              <div class="mb-6 p-4 rounded-lg border bg-white">
                <h4 class="text-sm font-semibold text-gray-700 mb-3">
                  Vulnerability Summary
                </h4>
                <div class="grid grid-cols-5 gap-3 text-center text-xs">
                  <div class="p-2 rounded bg-red-50 border border-red-200">
                    <div class="font-bold text-red-600 text-lg">
                      {{ sqliLiveResults.vulnerability_summary?.critical || 0 }}
                    </div>
                    <div class="text-red-500">Critical</div>
                  </div>
                  <div
                    class="p-2 rounded bg-orange-50 border border-orange-200"
                  >
                    <div class="font-bold text-orange-600 text-lg">
                      {{ sqliLiveResults.vulnerability_summary?.high || 0 }}
                    </div>
                    <div class="text-orange-500">High</div>
                  </div>
                  <div
                    class="p-2 rounded bg-yellow-50 border border-yellow-200"
                  >
                    <div class="font-bold text-yellow-600 text-lg">
                      {{ sqliLiveResults.vulnerability_summary?.medium || 0 }}
                    </div>
                    <div class="text-yellow-500">Medium</div>
                  </div>
                  <div class="p-2 rounded bg-blue-50 border border-blue-200">
                    <div class="font-bold text-blue-600 text-lg">
                      {{ sqliLiveResults.vulnerability_summary?.low || 0 }}
                    </div>
                    <div class="text-blue-500">Low</div>
                  </div>
                  <div class="p-2 rounded bg-gray-50 border border-gray-200">
                    <div class="font-bold text-gray-500 text-lg">
                      {{
                        sqliLiveResults.vulnerability_summary
                          ?.total_payloads_tested || 0
                      }}
                    </div>
                    <div class="text-gray-400">Total Payloads</div>
                  </div>
                </div>
              </div>

              <!-- Per-Form Results -->
              <div
                v-if="sqliLiveResults.forms && sqliLiveResults.forms.length > 0"
              >
                <h4 class="text-sm font-semibold text-gray-700 mb-3">
                  Form Details
                </h4>
                <div class="space-y-4">
                  <div
                    v-for="(form, formIdx) in sqliLiveResults.forms"
                    :key="'form-' + formIdx"
                    class="border rounded-lg bg-white overflow-hidden"
                    :class="{
                      'border-red-300':
                        form.status === 'Tested' && hasFormVulnerability(form),
                      'border-yellow-300':
                        form.status === 'Tested' && !hasFormVulnerability(form),
                      'border-gray-300': form.status !== 'Tested',
                      'border-blue-300': form.status === 'Skipped (Protected)',
                    }"
                  >
                    <!-- Form Header -->
                    <div
                      class="p-4 flex items-center justify-between"
                      :class="{
                        'bg-red-50':
                          form.status === 'Tested' &&
                          hasFormVulnerability(form),
                        'bg-gray-50':
                          form.status !== 'Tested' ||
                          !hasFormVulnerability(form),
                      }"
                    >
                      <div>
                        <div class="flex items-center gap-2 flex-wrap">
                          <span
                            class="text-xs font-mono bg-gray-200 px-2 py-0.5 rounded text-gray-700"
                            >{{ form.method }}</span
                          >
                          <span
                            class="text-sm font-medium text-gray-800 truncate max-w-md"
                            >{{ form.action }}</span
                          >
                        </div>
                        <!-- Iframe source URL -->
                        <div
                          v-if="form.source === 'iframe' && form.iframe_src"
                          class="text-xxs text-purple-500 mt-0.5 truncate max-w-md"
                        >
                          <Paperclip
                            class="w-3 h-3 inline-block mr-1 align-middle"
                          />
                          {{ form.iframe_src }}
                        </div>
                        <div class="flex items-center gap-3 mt-1">
                          <span class="text-xs text-gray-500">
                            {{ form.inputs_scanned }} input(s)
                          </span>

                          <!-- Iframe Source Badge -->
                          <span
                            v-if="form.source === 'iframe'"
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-semibold bg-purple-100 text-purple-700 border border-purple-300"
                            :title="form.iframe_src || 'Embedded'"
                          >
                            <FileText class="w-3 h-3" /> Iframe
                          </span>

                          <!-- CAPTCHA Protection Badge -->
                          <span
                            v-if="form.protection !== 'None'"
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300"
                          >
                            <ShieldCheck class="w-3 h-3 text-amber-700" />
                            reCAPTCHA Detected
                          </span>
                          <span
                            v-else
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wide bg-green-100 text-green-700 border border-green-300"
                          >
                            <Check class="w-3 h-3 text-green-700" /> No
                            reCAPTCHA
                          </span>
                        </div>
                      </div>
                      <span
                        class="shrink-0 px-2.5 py-1 rounded text-xs font-semibold"
                        :class="{
                          'bg-green-100 text-green-700':
                            form.status === 'Tested' &&
                            !hasFormVulnerability(form),
                          'bg-red-100 text-red-700':
                            form.status === 'Tested' &&
                            hasFormVulnerability(form),
                          'bg-blue-100 text-blue-700':
                            form.status === 'Skipped (Protected)',
                          'bg-yellow-100 text-yellow-700':
                            form.status === 'Skipped (External Domain)',
                          'bg-gray-100 text-gray-600': form.status === 'Error',
                        }"
                      >
                        {{ form.status }}
                      </span>
                    </div>

                    <!-- Protection Notice -->
                    <div
                      v-if="form.error"
                      class="px-4 py-2 bg-blue-50 text-xs text-blue-700"
                    >
                      <Info
                        class="w-3.5 h-3.5 text-blue-600 inline-block mr-1 align-middle"
                      />
                      {{ form.error }}
                    </div>

                    <!-- Payloads Table -->
                    <div
                      v-if="form.payloads && form.payloads.length > 0"
                      class="overflow-x-auto"
                    >
                      <table class="w-full text-xs">
                        <thead>
                          <tr class="bg-gray-100 text-gray-600">
                            <th class="text-left px-3 py-2 border-b">
                              Payload
                            </th>
                            <th class="text-center px-3 py-2 border-b w-24">
                              Result
                            </th>
                            <th class="text-center px-3 py-2 border-b w-20">
                              Severity
                            </th>
                            <th class="text-center px-3 py-2 border-b w-16">
                              Status
                            </th>
                            <th class="text-center px-3 py-2 border-b w-20">
                              Duration
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(p, pIdx) in form.payloads"
                            :key="'p-' + formIdx + '-' + pIdx"
                            class="border-b hover:bg-gray-50"
                            :class="{
                              'bg-red-50':
                                p.severity === 'Critical' ||
                                p.severity === 'High',
                              'bg-yellow-50': p.severity === 'Medium',
                            }"
                          >
                            <td
                              class="px-3 py-2 font-mono text-gray-800 break-all max-w-xs"
                            >
                              {{ p.payload }}
                            </td>
                            <td class="px-3 py-2 text-center">
                              <span
                                class="px-2 py-0.5 rounded text-xxs font-semibold"
                                :class="{
                                  'bg-red-100 text-red-700':
                                    p.result !== 'No Vulnerability Detected' &&
                                    p.result !== 'Error',
                                  'bg-green-100 text-green-700':
                                    p.result === 'No Vulnerability Detected',
                                  'bg-gray-100 text-gray-600':
                                    p.result === 'Error',
                                }"
                              >
                                {{ truncateResult(p.result) }}
                              </span>
                            </td>
                            <td class="px-3 py-2 text-center">
                              <span
                                v-if="p.severity !== 'None'"
                                class="text-xxs font-bold"
                                :class="{
                                  'text-red-600': p.severity === 'Critical',
                                  'text-orange-600': p.severity === 'High',
                                  'text-yellow-600': p.severity === 'Medium',
                                  'text-blue-600': p.severity === 'Low',
                                }"
                                >{{ p.severity }}</span
                              >
                              <span v-else class="text-gray-400">—</span>
                            </td>
                            <td class="px-3 py-2 text-center font-mono text-xs">
                              {{ p.status_code || "—" }}
                            </td>
                            <td class="px-3 py-2 text-center text-gray-500">
                              {{
                                p.duration_ms
                                  ? (p.duration_ms / 1000).toFixed(1) + "s"
                                  : "—"
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Evidence expander for vulnerable payloads -->
                    <div
                      v-if="getVulnerablePayloads(form).length > 0"
                      class="px-4 py-3 border-t bg-gray-50"
                    >
                      <details class="text-xs">
                        <summary
                          class="cursor-pointer font-semibold text-red-600 hover:text-red-800"
                        >
                          View Evidence ({{
                            getVulnerablePayloads(form).length
                          }}
                          finding(s))
                        </summary>
                        <div class="mt-2 space-y-2">
                          <div
                            v-for="(vp, vpIdx) in getVulnerablePayloads(form)"
                            :key="'vp-' + formIdx + '-' + vpIdx"
                            class="p-3 border rounded bg-white"
                            :class="{
                              'border-red-300':
                                vp.severity === 'Critical' ||
                                vp.severity === 'High',
                              'border-yellow-300': vp.severity === 'Medium',
                            }"
                          >
                            <div class="flex items-center justify-between mb-1">
                              <code class="text-gray-800 font-mono">{{
                                vp.payload
                              }}</code>
                              <span
                                class="px-2 py-0.5 rounded text-xxs font-bold"
                                :class="{
                                  'bg-red-100 text-red-700':
                                    vp.severity === 'Critical',
                                  'bg-orange-100 text-orange-700':
                                    vp.severity === 'High',
                                  'bg-yellow-100 text-yellow-700':
                                    vp.severity === 'Medium',
                                }"
                                >{{ vp.severity }}</span
                              >
                            </div>
                            <div class="text-gray-600 leading-relaxed">
                              {{ vp.evidence }}
                            </div>
                            <div class="mt-1 text-gray-400">
                              Response: {{ vp.response_length }} bytes &middot;
                              {{ (vp.duration_ms / 1000).toFixed(1) }}s &middot;
                              HTTP {{ vp.status_code }}
                            </div>
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No forms found -->
              <div v-else class="text-center py-6 text-gray-400">
                <span class="inline-flex items-center gap-1.5"
                  ><CheckCircle2 class="w-4 h-4 text-green-500" /> No HTML forms
                  found on the page. Nothing to test.</span
                >
              </div>

              <!-- Scan Metadata -->
              <div class="mt-4 text-xs text-gray-400 text-right">
                Scanned {{ sqliLiveResults.scanned_at || "" }} &middot; Duration
                {{ (sqliLiveResults.scan_duration_ms / 1000).toFixed(1) }}s
              </div>
            </div>

            <!-- ── No live results yet: show prompt or code-scan fallback ── -->
            <div
              v-else-if="!sqliLiveResults || sqliLiveResults.status === 'ERROR'"
            >
              <div
                v-if="sqliLiveResults?.status === 'ERROR'"
                class="text-center py-4 text-red-500 bg-red-50 rounded mb-4 flex items-center justify-center gap-1.5"
              >
                <AlertTriangle class="w-4 h-4" />
                <span>{{
                  sqliLiveResults.error || "Live scan encountered an error."
                }}</span>
              </div>

              <!-- Fallback: show code-level SQLi scan from project audit -->
              <div
                v-if="
                  auditResults &&
                  auditResults.results?.sql_injection &&
                  auditResults.results.sql_injection.length > 0
                "
              >
                <div class="mb-4 p-4 rounded-lg border bg-white">
                  <span class="block text-2xl font-bold text-gray-700">
                    {{ auditResults.summary.sql_injection_count || 0 }}
                  </span>
                  <span
                    class="text-xs font-medium uppercase tracking-wider text-gray-500"
                    >Code-Level SQL Injection Risks (Static Analysis)</span
                  >
                </div>
                <div class="grid grid-cols-1 gap-3 mb-4">
                  <div
                    v-for="(item, index) in paginatedSql"
                    :key="index"
                    class="p-3 border rounded bg-white text-xs"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <div class="font-semibold text-red-600 capitalize">
                          {{ item.type }}
                        </div>
                        <div class="mt-1 text-gray-500 text-xxs">
                          {{ item.file }}:{{ item.line }}
                        </div>
                        <div
                          class="mt-2 p-2 bg-gray-900 text-gray-100 font-mono rounded overflow-x-auto whitespace-pre"
                        >
                          {{ item.snippet }}
                        </div>
                        <div class="mt-2 text-gray-700">
                          <strong>Recommendation:</strong>
                          {{ item.recommendation }}
                        </div>
                      </div>
                      <span
                        class="shrink-0 px-2 py-1 rounded text-xxs font-semibold uppercase bg-red-100 text-red-700"
                      >
                        {{ item.severity }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="flex justify-between items-center mt-4 text-xs">
                  <button
                    @click="auditPagination.sql--"
                    :disabled="auditPagination.sql === 1"
                    class="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span
                    >Page {{ auditPagination.sql }} of
                    {{
                      Math.max(
                        1,
                        Math.ceil(
                          (auditResults.results?.sql_injection?.length || 0) /
                            auditItemsPerPage
                        )
                      )
                    }}</span
                  >
                  <button
                    @click="auditPagination.sql++"
                    :disabled="
                      auditPagination.sql >=
                      Math.max(
                        1,
                        Math.ceil(
                          (auditResults.results?.sql_injection?.length || 0) /
                            auditItemsPerPage
                        )
                      )
                    "
                    class="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>

              <!-- No results at all -->
              <div v-else class="text-center py-8 text-gray-400">
                <div class="flex justify-center mb-3">
                  <ShieldCheck class="w-12 h-12 text-gray-300" />
                </div>
                <p class="font-medium">No SQL injection scan results yet.</p>
                <p class="text-xs mt-1">
                  Click "Start Deep Scan" to test this website's forms for SQL
                  injection vulnerabilities using browser automation.
                </p>
              </div>
            </div>
          </div>

          <!-- Crawl Indexing Tab -->
          <div
            v-else-if="activeTab === 'Crawl Indexing'"
            class="resource-audit-content"
          >
            <div v-if="!auditResults" class="text-center py-10 text-gray-500">
              <div class="animate-pulse flex flex-col items-center">
                <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
                <p>Loading crawl indexing results...</p>
              </div>
            </div>
            <div v-else>
              <!-- Contextual Banner for Server Log Inspection -->
              <div
                class="mb-6 p-4 border border-blue-100 rounded-lg bg-gradient-to-r from-blue-50/70 to-indigo-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
              >
                <div class="flex items-center gap-3">
                  <Bot class="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <h4 class="font-semibold text-gray-800">
                      Looking for Server Log Crawl Inspection?
                    </h4>
                    <p class="text-gray-500">
                      You can upload and inspect your server access logs
                      (Apache, Nginx, Cloudflare, IIS) in the dedicated
                      <strong class="text-blue-700">Index by Google</strong> tab
                      above.
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    activeGooglebotData &&
                    activeGooglebotData.has_googlebot_data
                  "
                  class="flex-shrink-0"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800"
                  >
                    <CheckCircle2 class="w-3.5 h-3.5" /> Googlebot verified in
                    logs
                  </span>
                </div>
              </div>
              <!-- Summary Cards -->
              <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <!-- robots.txt -->
                <div
                  class="p-4 rounded-lg border bg-white transition-all hover:shadow-md"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-gray-700"
                      >robots.txt</span
                    >
                    <span
                      :class="{
                        'text-green-500': crawlIndexingData.robots_txt?.exists,
                        'text-red-500': !crawlIndexingData.robots_txt?.exists,
                      }"
                      class="text-lg"
                    >
                      <CheckCircle2
                        v-if="crawlIndexingData.robots_txt?.exists"
                        class="w-5 h-5 text-green-500"
                      />
                      <XCircle v-else class="w-5 h-5 text-red-500" />
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 space-y-1">
                    <p v-if="crawlIndexingData.robots_txt?.exists">
                      Googlebot:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.robots_txt.googlebot_rules ===
                            'allowed',
                          'text-red-600':
                            crawlIndexingData.robots_txt.googlebot_rules ===
                            'blocked',
                        }"
                        class="font-medium capitalize"
                      >
                        {{ crawlIndexingData.robots_txt.googlebot_rules }}
                      </span>
                    </p>
                    <p v-if="crawlIndexingData.robots_txt?.exists">
                      Sitemap refs:
                      <span class="font-medium text-gray-700">
                        {{
                          crawlIndexingData.robots_txt.sitemap_references
                            ?.length || 0
                        }}
                      </span>
                    </p>
                    <p v-else class="text-red-500 font-medium">
                      File not found
                    </p>
                  </div>
                </div>

                <!-- XML Sitemap -->
                <div
                  class="p-4 rounded-lg border bg-white transition-all hover:shadow-md"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-gray-700"
                      >XML Sitemap</span
                    >
                    <span
                      :class="{
                        'text-green-500': crawlIndexingData.xml_sitemap?.exists,
                        'text-red-500': !crawlIndexingData.xml_sitemap?.exists,
                      }"
                      class="text-lg"
                    >
                      <CheckCircle2
                        v-if="crawlIndexingData.xml_sitemap?.exists"
                        class="w-5 h-5 text-green-500"
                      />
                      <XCircle v-else class="w-5 h-5 text-red-500" />
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 space-y-1">
                    <p v-if="crawlIndexingData.xml_sitemap?.exists">
                      Type:
                      <span class="font-medium text-gray-700 capitalize">
                        {{ crawlIndexingData.xml_sitemap.sitemap_type }}
                      </span>
                    </p>
                    <p v-if="crawlIndexingData.xml_sitemap?.exists">
                      Entries:
                      <span class="font-medium text-gray-700">
                        {{ crawlIndexingData.xml_sitemap.entry_count }}
                      </span>
                    </p>
                    <p v-else class="text-red-500 font-medium">Not found</p>
                  </div>
                </div>

                <!-- HTML Sitemap -->
                <div
                  class="p-4 rounded-lg border bg-white transition-all hover:shadow-md"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-gray-700"
                      >HTML Sitemap</span
                    >
                    <span
                      :class="{
                        'text-green-500':
                          crawlIndexingData.html_sitemap?.exists,
                        'text-red-500': !crawlIndexingData.html_sitemap?.exists,
                      }"
                      class="text-lg"
                    >
                      <CheckCircle2
                        v-if="crawlIndexingData.html_sitemap?.exists"
                        class="w-5 h-5 text-green-500"
                      />
                      <XCircle v-else class="w-5 h-5 text-red-500" />
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 space-y-1">
                    <p v-if="crawlIndexingData.html_sitemap?.exists">
                      Links:
                      <span class="font-medium text-gray-700">
                        {{ crawlIndexingData.html_sitemap.link_count }}
                      </span>
                    </p>
                    <p v-else class="text-red-500 font-medium">Not found</p>
                  </div>
                </div>

                <!-- Indexability Status -->
                <div
                  class="p-4 rounded-lg border bg-white transition-all hover:shadow-md"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-gray-700"
                      >Indexability</span
                    >
                    <span
                      :class="{
                        'text-green-500':
                          !crawlIndexingData.indexed_by_google?.noindex_meta &&
                          !crawlIndexingData.indexed_by_google
                            ?.noindex_header &&
                          crawlIndexingData.robots_txt?.googlebot_rules !==
                            'blocked',
                        'text-amber-500':
                          crawlIndexingData.robots_txt?.googlebot_rules ===
                          'blocked',
                        'text-red-500':
                          crawlIndexingData.indexed_by_google?.noindex_meta ||
                          crawlIndexingData.indexed_by_google?.noindex_header,
                      }"
                      class="text-lg"
                    >
                      <XCircle
                        v-if="
                          crawlIndexingData.indexed_by_google?.noindex_meta ||
                          crawlIndexingData.indexed_by_google?.noindex_header
                        "
                        class="w-5 h-5 text-red-500"
                      />
                      <AlertTriangle
                        v-else-if="
                          crawlIndexingData.robots_txt?.googlebot_rules ===
                          'blocked'
                        "
                        class="w-5 h-5 text-amber-500"
                      />
                      <CheckCircle2 v-else class="w-5 h-5 text-green-500" />
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 space-y-1">
                    <p
                      v-if="
                        !crawlIndexingData.indexed_by_google?.noindex_meta &&
                        !crawlIndexingData.indexed_by_google?.noindex_header &&
                        crawlIndexingData.robots_txt?.googlebot_rules !==
                          'blocked'
                      "
                      class="text-green-600 font-medium"
                    >
                      Eligible for indexing
                    </p>
                    <p
                      v-if="
                        crawlIndexingData.indexed_by_google?.canonical_present
                      "
                    >
                      Canonical:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.indexed_by_google
                              .canonical_correct,
                          'text-red-600':
                            !crawlIndexingData.indexed_by_google
                              .canonical_correct,
                        }"
                        class="font-medium"
                      >
                        {{
                          crawlIndexingData.indexed_by_google.canonical_correct
                            ? "Correct"
                            : "Wrong domain"
                        }}
                      </span>
                    </p>
                    <p v-else class="text-gray-400">No canonical tag</p>
                    <p
                      v-if="crawlIndexingData.indexed_by_google?.noindex_meta"
                      class="text-red-600 font-medium"
                    >
                      noindex meta tag
                    </p>
                    <p
                      v-if="crawlIndexingData.indexed_by_google?.noindex_header"
                      class="text-red-600 font-medium"
                    >
                      X-Robots-Tag: noindex
                    </p>
                    <p
                      v-if="
                        crawlIndexingData.robots_txt?.googlebot_rules ===
                        'blocked'
                      "
                      class="text-amber-600 font-medium"
                    >
                      Robots.txt blocks Googlebot
                    </p>

                    <!-- Googlebot Crawl Activity (from access log) -->
                    <div
                      v-if="
                        activeGooglebotData &&
                        activeGooglebotData.has_googlebot_data
                      "
                      class="mt-3 pt-3 border-t border-gray-100"
                    >
                      <p class="text-xs font-semibold text-gray-600 mb-2">
                        <Search
                          class="w-3.5 h-3.5 text-blue-600 inline-block mr-1 align-text-bottom"
                        />
                        CONFIRMED GOOGLEBOT ACTIVITY
                      </p>
                      <p>
                        <strong>Last crawled:</strong>
                        <span class="text-green-600 font-medium">
                          {{ activeGooglebotData.summary.last_crawl }}
                        </span>
                      </p>
                      <p>
                        <strong>First crawl:</strong>
                        <span class="text-gray-600">
                          {{ activeGooglebotData.summary.first_crawl }}
                        </span>
                      </p>
                      <p>
                        <strong>Total visits:</strong>
                        <span class="text-gray-700 font-medium">
                          {{ activeGooglebotData.summary.total_visits }}
                          ({{ activeGooglebotData.summary.desktop_visits }}
                          desktop,
                          {{ activeGooglebotData.summary.mobile_visits }}
                          mobile)
                        </span>
                      </p>
                      <p>
                        <strong>Pages crawled:</strong>
                        <span class="text-gray-700">{{
                          activeGooglebotData.summary.unique_pages
                        }}</span>
                      </p>
                      <div
                        v-if="
                          activeGooglebotData.pages_crawled &&
                          activeGooglebotData.pages_crawled.length
                        "
                        class="mt-1"
                      >
                        <span class="text-gray-500">URLs:</span>
                        <ul class="list-disc list-inside text-gray-600 ml-2">
                          <li
                            v-for="page in activeGooglebotData.pages_crawled.slice(
                              0,
                              10
                            )"
                            :key="page"
                            class="break-all"
                          >
                            {{ page }}
                          </li>
                          <li
                            v-if="activeGooglebotData.pages_crawled.length > 10"
                            class="text-gray-400"
                          >
                            ...and
                            {{ activeGooglebotData.pages_crawled.length - 10 }}
                            more pages
                          </li>
                        </ul>
                      </div>
                      <p class="text-green-600 font-semibold mt-2">
                        <CheckCircle2
                          class="w-4 h-4 text-green-600 inline-block mr-1 align-text-bottom"
                        />
                        Googlebot confirmed in server logs.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- HTTP Caching -->
                <div
                  class="p-4 rounded-lg border bg-white transition-all hover:shadow-md"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-gray-700"
                      >HTTP Caching</span
                    >
                    <span
                      :class="{
                        'text-green-500':
                          crawlIndexingData.cached_by_google?.has_caching ||
                          crawlIndexingData.cached_by_google?.cache_control ||
                          crawlIndexingData.cached_by_google?.etag,
                        'text-amber-500':
                          !crawlIndexingData.cached_by_google?.has_caching &&
                          !crawlIndexingData.cached_by_google?.cache_control &&
                          !crawlIndexingData.cached_by_google?.etag &&
                          (crawlIndexingData.cached_by_google?.expires ||
                            crawlIndexingData.cached_by_google?.last_modified),
                        'text-red-500':
                          !crawlIndexingData.cached_by_google?.has_caching &&
                          !crawlIndexingData.cached_by_google?.cache_control &&
                          !crawlIndexingData.cached_by_google?.etag &&
                          !crawlIndexingData.cached_by_google?.expires &&
                          !crawlIndexingData.cached_by_google?.last_modified,
                      }"
                      class="text-lg"
                    >
                      <CheckCircle2
                        v-if="
                          crawlIndexingData.cached_by_google?.has_caching ||
                          crawlIndexingData.cached_by_google?.cache_control ||
                          crawlIndexingData.cached_by_google?.etag
                        "
                        class="w-5 h-5 text-green-500"
                      />
                      <AlertTriangle
                        v-else-if="
                          crawlIndexingData.cached_by_google?.expires ||
                          crawlIndexingData.cached_by_google?.last_modified
                        "
                        class="w-5 h-5 text-amber-500"
                      />
                      <XCircle v-else class="w-5 h-5 text-red-500" />
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 space-y-1">
                    <p>
                      Cache-Control:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.cached_by_google?.cache_control,
                          'text-red-600':
                            !crawlIndexingData.cached_by_google?.cache_control,
                        }"
                        class="font-medium"
                      >
                        {{
                          crawlIndexingData.cached_by_google?.cache_control
                            ? "Configured"
                            : "Missing"
                        }}
                      </span>
                    </p>
                    <p>
                      ETag:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.cached_by_google?.etag,
                          'text-gray-400':
                            !crawlIndexingData.cached_by_google?.etag,
                        }"
                        class="font-medium"
                      >
                        {{
                          crawlIndexingData.cached_by_google?.etag
                            ? "Present"
                            : "None"
                        }}
                      </span>
                    </p>
                    <p>
                      Last-Modified:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.cached_by_google?.last_modified,
                          'text-gray-400':
                            !crawlIndexingData.cached_by_google?.last_modified,
                        }"
                        class="font-medium"
                      >
                        {{
                          crawlIndexingData.cached_by_google?.last_modified
                            ? "Present"
                            : "None"
                        }}
                      </span>
                    </p>
                    <p>
                      Expires:
                      <span
                        :class="{
                          'text-green-600':
                            crawlIndexingData.cached_by_google?.expires,
                          'text-gray-400':
                            !crawlIndexingData.cached_by_google?.expires,
                        }"
                        class="font-medium"
                      >
                        {{
                          crawlIndexingData.cached_by_google?.expires
                            ? "Present"
                            : "None"
                        }}
                      </span>
                    </p>
                    <p class="text-[10px] text-gray-400 pt-1">
                      Browser & CDN cache policy (RFC 9111)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Results cached indicator & Re-scan -->
            <div
              v-if="auditResults?.cached_at"
              class="mt-4 flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs"
            >
              <div class="flex items-center gap-2 text-gray-600">
                <Package class="w-4 h-4 text-blue-600" />
                <span
                  >Results cached from
                  {{ new Date(auditResults.cached_at).toLocaleString() }}</span
                >
              </div>
              <button
                @click="$emit('re-scan')"
                :disabled="resultsStillProcessing"
                v-tippy="{
                  content: resultsStillProcessing
                    ? 'Certain Results are still processing'
                    : '',
                  theme: 'diagnostic',
                  placement: 'top',
                }"
                class="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  class="w-3.5 h-3.5 inline-block mr-1 align-text-bottom"
                />
                Re-scan
              </button>
            </div>

            <!-- Issues List -->
            <div v-if="crawlIndexingData.issues?.length > 0" class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-700 mb-2">
                Issues Found
              </h3>
              <div
                v-for="(issue, index) in crawlIndexingData.issues"
                :key="index"
                class="p-3 border-l-4 rounded-md bg-white shadow-sm"
                :class="{
                  'border-red-500 bg-red-50': issue.severity === 'high',
                  'border-yellow-500 bg-yellow-50': issue.severity === 'medium',
                  'border-green-500 bg-green-50': issue.severity === 'low',
                }"
              >
                <div class="flex items-start gap-2">
                  <span
                    class="shrink-0 px-2 py-0.5 rounded text-xxs font-semibold uppercase text-white"
                    :class="{
                      'bg-red-500': issue.severity === 'high',
                      'bg-yellow-500': issue.severity === 'medium',
                      'bg-green-500': issue.severity === 'low',
                    }"
                  >
                    {{ issue.severity }}
                  </span>
                  <div class="min-w-0">
                    <p class="text-sm text-gray-700">
                      {{ issue.description }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ issue.recommendation }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- All Clear -->
            <div v-else class="text-center py-8 text-gray-400">
              <span class="inline-flex items-center gap-1.5"
                ><CheckCircle2 class="w-4 h-4 text-green-500" /> All crawl
                indexing checks passed.</span
              >
            </div>
          </div>
        </div>

        <!-- Issues to Fix Section Below Resource Audit Details -->
      </div>
    </div>

    <!-- Right: Chart Sidebar -->
    <!-- <div class="col-span-3 bg-white rounded text-sm max-h-96">
      <h3 class="font-semibold mb-2 text-base p-8">Validation Summary</h3>
      <PieChart :chart-data="chartData" />
    </div> -->
    <!-- Issues to Fix Section -->
    <div class="col-span-12 mt-4">
      <div class="issues-to-fix bg-white p-4 rounded-md">
        <h3 class="text-base font-semibold mb-2">
          <AlertOctagon
            class="w-4 h-4 text-red-600 inline-block mr-1 align-text-bottom"
          />
          Issues to Fix
        </h3>
        <div v-if="hasIssues" class="space-y-3">
          <div
            v-for="(issue, index) in filteredIssuesToFix"
            :key="index"
            class="flex items-center p-3 border-l-4 rounded-md shadow-sm transition-all duration-200 hover:shadow-lg"
            :class="{
              'bg-red-50 border-red-500': issue.priority === 'HIGH',
              'bg-yellow-50 border-yellow-500': issue.priority === 'MEDIUM',
              'bg-green-50 border-green-500': issue.priority === 'LOW',
            }"
          >
            <span
              class="text-xs font-bold px-3 py-1 min-w-20 text-center rounded"
              :class="{
                'bg-red-500 text-white': issue.priority === 'HIGH',
                'bg-yellow-500 text-white': issue.priority === 'MEDIUM',
                'bg-green-500 text-white': issue.priority === 'LOW',
              }"
            >
              {{ issue.priority }}
            </span>

            <p class="ml-4 text-gray-700 flex items-center">
              <a class="hover:text-blue-700">
                {{ issue.message }}
              </a>
            </p>
          </div>
        </div>
        <p v-else class="text-gray-500">
          <span class="inline-flex items-center gap-1.5"
            ><CheckCircle2 class="w-4 h-4 text-green-500" /> No critical issues
            found.</span
          >
        </p>
      </div>

      <!-- Metadata Issues -->
      <div
        v-if="activeTab === 'Meta Data'"
        ref="metadataIssues"
        class="metadata-issues bg-white p-4 rounded-md mt-4"
        :class="{ 'highlight-section': highlight === 'metadata' }"
      >
        <h3 class="text-base font-semibold mb-2">
          <FileText
            class="w-4 h-4 text-blue-600 inline-block mr-1 align-text-bottom"
          />
          Metadata Issues
        </h3>
        <PageIssuesPanel
          :issues="currentPage?.issues || []"
          section="metadata"
        />
      </div>

      <!-- SOP Compliance Issues -->
      <div
        v-if="activeTab === 'SOP Compliance'"
        ref="sopIssues"
        class="sop-issues bg-white p-4 rounded-md mt-4"
        :class="{ 'highlight-section': highlight === 'sop' }"
      >
        <h3 class="text-sm font-semibold mb-2">
          <Building2
            class="w-4 h-4 text-indigo-600 inline-block mr-1 align-text-bottom"
          />
          SOP Compliance Issues
        </h3>
        <!-- <div v-if="sopIssues.length > 0" class="space-y-3">
          <div
            v-for="(issue, index) in sopIssues"
            :key="index"
            ref="sopIssueRefs"
            class="p-4 border rounded-md shadow-sm transition-all duration-300"
            :class="{
              'border-red-500 bg-red-50': issue.priority === 'HIGH',
              'border-yellow-500 bg-yellow-50': issue.priority === 'MEDIUM',
              'border-green-500 bg-green-50': issue.priority === 'LOW',
              highlighted:
                highlightedIssue === 'sop' && highlightedIndex === index,
            }"
          >
            <div class="flex items-center mb-2">
              <span class="text-lg mr-2">
                <i
                  v-if="issue.priority === 'HIGH'"
                  class="fa-solid fa-radiation text-red-800"
                ></i>
                <i
                  v-if="issue.priority === 'MEDIUM'"
                  class="fa-solid fa-triangle-exclamation text-yellow-500"
                ></i>
                <i
                  v-if="issue.priority === 'LOW'"
                  class="fa-solid fa-triangle-exclamation text-green-500"
                ></i>
              </span>
              <h4 class="text-lg font-bold text-gray-800">{{ issue.message }}</h4>
            </div>
            <p class="text-gray-700 text-sm">
              <strong>Why this matters:</strong>
              {{ getExplanation(issue) || "No explanation provided." }}
            </p>
            <a
              :href="issue.link"
              target="_blank"
              class="text-blue-500 underline mt-2 inline-block"
            >
              Learn More
            </a>
          </div>
        </div> -->
        <PageIssuesPanel :issues="currentPage?.issues || []" section="sop" />
        <!-- <PageIssuesPanel :issues="currentPage.issues || []" section="sop" /> -->
      </div>
    </div>
  </div>
</template>
<script>
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";

import PageIssuesPanel from "@/components/validation/PageIssuesPanel.vue";
import RedundantResources from "@/components/RedundantResources.vue";
import XssRiskPanel from "@/components/XssRiskPanel.vue";
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Smartphone,
  Monitor,
  Image as ImageIcon,
  ShieldCheck,
  Search,
  Bot,
  FileText,
  Paperclip,
  Info,
  Package,
  AlertOctagon,
  Building2,
  Lightbulb,
  Clock,
} from "lucide-vue-next";
import axios from "axios";
import baseUrl from "../config";

ChartJS.register(Title, Tooltip, Legend, ArcElement);
export default {
  name: "ValidationResults",
  components: {
    PageIssuesPanel,
    RedundantResources,
    XssRiskPanel,
    ChevronUp,
    ChevronDown,
    ChevronRight,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Check,
    X,
    RefreshCw,
    Smartphone,
    Monitor,
    ImageIcon,
    ShieldCheck,
    Search,
    Bot,
    FileText,
    Paperclip,
    Info,
    Package,
    AlertOctagon,
    Building2,
    Lightbulb,
    Clock,
  },
  props: {
    results: {
      type: Object,
      default: () => ({ pages: [] }),
    },
    auditResults: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    googlebotLogData: {
      type: Object,
      default: null,
    },
    auditLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPage: null,
      scanError: "",
      activeTab: "Meta Data",
      tabs: [
        "Meta Data",
        "Performance",
        "SOP Compliance",
        "Social Media",
        "Schema Markup",
        "Resource Audit",
        "Redundant Resources",
        "Grammar/Spelling",
        "Navigation Dropdowns",
        "XSS Risks",
        "SQL Injection",
        "Crawl Indexing",
      ],
      filters: {
        titleStatus: "",
        metaStatus: "",
        https: "",
        duplicateTitle: "",
        duplicateMainContent: "",
      },
      searchQuery: "",
      highlight: null,
      highlightedIssue: null,
      highlightedIndex: null,
      passedChecks: 0,
      totalChecks: 0,
      activeAuditTab: "css",
      auditPagination: {
        css: 1,
        js: 1,
        images: 1,
        grammar: 1,
        sql: 1,
      },
      auditItemsPerPage: 10,
      contactLinkFilter: "all",
      expandedMinified: {},
      collapsedMinified: false,
      collapsedContactLink: false,
      collapsedExternalLinks: false,
      collapsedLocalLinks: false,
      collapsedProfanity: false,
      collapsedImageFilenames: false,
      collapsedImageAlt: false,
      collapsedImageSize: false,
      collapsedW3C: false,
      collapsedAdditionalSop: false,
      collapsedBrokenLinks: false,
      sopItemsPerPage: 10,
      schemaShowRaw: false,
      sopPagination: {
        externalLinks: 1,
        localLinks: 1,
        profanity: 1,
        minified: 1,
        contactLink: 1,
        imageFilenames: 1,
        imageAlt: 1,
        imageSize: 1,
        w3c: 1,
        brokenLinks: 1,
      },
      // ── SQLi Live Scanner state ──────────────────────────────────────
      sqliLiveResults: null,
      sqliScanning: false,
      sqliScanError: "",
      accessLogFile: null,
      accessLogUploading: false,
      accessLogError: "",
      accessLogSuccess: "",
      // ── Per-row scan tracking ────────────────────────────────────
      scanningInProgress: false,
      scanningUrl: null,
      scanCompleteUrl: "",
      scanCompleteTimer: null,
      scanSkippedMessage: "",
      // ── Table sorting ───────────────────────────────────────────
      sortColumn: null,
      sortDirection: "asc",
      externalLinksSortColumn: null,
      externalLinksSortDirection: "asc",
      localLinksSortColumn: null,
      localLinksSortDirection: "asc",
      profanitySortColumn: null,
      profanitySortDirection: "asc",
      minifiedSortColumn: null,
      minifiedSortDirection: "asc",
      contactLinkSortColumn: null,
      contactLinkSortDirection: "asc",
      imageFilenameSortColumn: null,
      imageFilenameSortDirection: "asc",
      imageAltSortColumn: null,
      imageAltSortDirection: "asc",
      imageSizeSortColumn: null,
      imageSizeSortDirection: "asc",
      w3cIssuesSortColumn: null,
      w3cIssuesSortDirection: "asc",
      brokenLinksSortColumn: null,
      brokenLinksSortDirection: "asc",
      additionalSopSortColumn: null,
      additionalSopSortDirection: "asc",
    };
  },
  computed: {
    resultsStillProcessing() {
      return this.auditLoading || this.sqliScanning || this.scanningInProgress;
    },
    auditTabs() {
      if (!this.auditResults) return [];
      const tabsList = [
        {
          id: "css",
          name: "Unused CSS",
          count: this.auditResults.summary.unused_css_count,
        },
        {
          id: "js",
          name: "Unused JS",
          count: this.auditResults.summary.unused_js_functions_count,
        },
        {
          id: "images",
          name: "Orphan Images",
          count: this.auditResults.summary.unused_images_count,
        },
      ];
      if (this.auditResults.summary?.sql_injection_count !== undefined) {
        tabsList.push({
          id: "sql",
          name: "SQL Injection",
          count: this.auditResults.summary.sql_injection_count,
        });
      }
      return tabsList;
    },
    paginatedCss() {
      if (!this.auditResults?.results?.css) return [];
      const start = (this.auditPagination.css - 1) * this.auditItemsPerPage;
      return this.auditResults.results.css.slice(
        start,
        start + this.auditItemsPerPage
      );
    },
    paginatedJs() {
      if (!this.auditResults?.results?.js_functions) return [];
      const start = (this.auditPagination.js - 1) * this.auditItemsPerPage;
      return this.auditResults.results.js_functions.slice(
        start,
        start + this.auditItemsPerPage
      );
    },

    paginatedImages() {
      if (!this.auditResults?.results?.images) return [];
      const start = (this.auditPagination.images - 1) * this.auditItemsPerPage;
      return this.auditResults.results.images.slice(
        start,
        start + this.auditItemsPerPage
      );
    },
    paginatedSql() {
      if (!this.auditResults?.results?.sql_injection) return [];
      const start = (this.auditPagination.sql - 1) * this.auditItemsPerPage;
      return this.auditResults.results.sql_injection.slice(
        start,
        start + this.auditItemsPerPage
      );
    },
    grammarSpellingIssues() {
      return this.auditResults?.results?.grammar_spelling?.issues || [];
    },
    wwwRedirectData() {
      return (
        this.auditResults?.results?.crawl_indexing?.www_redirect || {
          status: "not_checked",
          is_consistent: false,
          preferred_version: "unknown",
          requested_url: null,
          counterpart_url: null,
          requested_response: null,
          counterpart_response: null,
          score: 0,
        }
      );
    },
    httpsRedirectData() {
      return (
        this.auditResults?.results?.crawl_indexing?.https_redirect || {
          status: "not_checked",
          is_secure: false,
          http_url: null,
          https_url: null,
          http_response: null,
          score: 0,
        }
      );
    },
    httpsRedirectRows() {
      const row = {
        version: "HTTP",
        initialUrl: this.httpsRedirectData?.http_url || "—",
        finalUrl: this.httpsRedirectData?.http_response?.final_url || "—",
        status: this.httpsRedirectData?.http_response?.status ?? "?",
        statusCode: this.httpsRedirectData?.http_response?.status ?? 0,
        isSecure: this.httpsRedirectData?.is_secure,
      };
      return [row];
    },
    sortedHttpsRedirectRows() {
      const arr = [...this.httpsRedirectRows];
      if (!this.httpsRedirectSortColumn) return arr;
      const dir = this.httpsRedirectSortDirection === "asc" ? 1 : -1;
      return arr.sort((a, b) => {
        let valA, valB;
        switch (this.httpsRedirectSortColumn) {
          case "version":
            valA = (a.version || "").toLowerCase();
            valB = (b.version || "").toLowerCase();
            break;
          case "initialUrl":
            valA = (a.initialUrl || "").toLowerCase();
            valB = (b.initialUrl || "").toLowerCase();
            break;
          case "finalUrl":
            valA = (a.finalUrl || "").toLowerCase();
            valB = (b.finalUrl || "").toLowerCase();
            break;
          case "status":
            valA = a.statusCode;
            valB = b.statusCode;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    performanceData() {
      return (
        this.auditResults?.results?.performance || {
          check: "performance",
          status: "Failed",
          url: null,
          mobile: null,
          desktop: null,
        }
      );
    },
    activeGooglebotData() {
      return (
        this.googlebotLogData ||
        this.crawlIndexingData?.indexed_by_google?.googlebot_data ||
        null
      );
    },
    crawlIndexingData() {
      return (
        this.auditResults?.results?.crawl_indexing || {
          robots_txt: {
            exists: false,
            url: "",
            sitemap_references: [],
            googlebot_rules: "not specified",
            content: null,
          },
          xml_sitemap: {
            exists: false,
            url: null,
            sitemap_type: null,
            entry_count: 0,
          },
          html_sitemap: { exists: false, url: null, link_count: 0 },
          indexed_by_google: {
            accessible: true,
            is_indexable: true,
            indexability_status: "indexable",
            noindex_meta: false,
            noindex_header: false,
            canonical_present: false,
            canonical_correct: true,
            canonical_url: null,
            googlebot_data: null,
          },
          cached_by_google: {
            has_caching: false,
            is_optimized: false,
            cache_control: false,
            etag: false,
            expires: false,
            last_modified: false,
          },
          issues: [],
        }
      );
    },
    navigationDropdownReport() {
      return this.auditResults?.results?.navigation_dropdowns || null;
    },
    paginatedGrammar() {
      const start = (this.auditPagination.grammar - 1) * this.auditItemsPerPage;
      return this.grammarSpellingIssues.slice(
        start,
        start + this.auditItemsPerPage
      );
    },
    compliancePercentage() {
      if (this.totalChecks === 0) return 0;
      return Math.round((this.passedChecks / this.totalChecks) * 100);
    },
    // How many SOP fields are valid
    sopCompliancePassed() {
      if (!this.currentPage?.sop) return 0;
      let count = 0;
      if (this.currentPage.sop.phone_format_valid) count++;
      if (this.currentPage.sop.email_format_valid) count++;
      if (this.currentPage.sop.address_format_valid) count++;
      if (this.currentPage.sop.company_name_valid) count++;
      if (this.currentPage.sop.copyright_year_valid) count++;
      if (this.currentPage.sop.email_mailto_valid) count++;
      if (this.currentPage.sop.tel_link_valid) count++;
      if (this.currentPage.sop.back_to_top_valid) count++;
      if (this.currentPage.sop.external_links_valid) count++;
      // Undefined (pre-feature history) counts as passed for backward compat
      if (this.currentPage.sop.local_links_valid !== false) count++;
      if (this.currentPage.sop.profanity_valid !== false) count++;
      if (this.currentPage.sop.minified_assets_valid) count++;
      if (this.currentPage.sop.image_filenames_valid) count++;
      if (this.currentPage.sop.image_alt_valid) count++;
      if (this.currentPage.sop.image_size_valid) count++;
      if (this.currentPage.sop.google_analytics_valid) count++;
      if (this.currentPage.sop.w3c_valid) count++;
      if (this.currentPage.sop.additional_sop_valid) count++;
      if (this.currentPage.sop.broken_links_valid) count++;
      if (this.currentPage.sop.schema_markup_valid) count++;
      if (this.wwwRedirectData?.is_consistent) count++;
      if (this.httpsRedirectData?.is_secure) count++;
      return count;
    },
    // Total SOP checks (22 in total)
    sopComplianceTotal() {
      return 22;
    },
    // Calculate SOP compliance percentage
    sopCompliancePercentage() {
      if (this.sopComplianceTotal === 0) return 0;
      return Math.round(
        (this.sopCompliancePassed / this.sopComplianceTotal) * 100
      );
    },
    // ── Blog/News Section Detection ──────────────────────────
    blogNewsData() {
      return this.currentPage?.blog_news_section || {};
    },
    blogNewsHasSection() {
      return this.blogNewsData.has_blog_news_section || false;
    },
    blogNewsScore() {
      return this.blogNewsData.confidence_score || 0;
    },
    blogNewsTypes() {
      return this.blogNewsData.detected_section_type || [];
    },
    blogNewsSignals() {
      return this.blogNewsData.signals || null;
    },
    crawledCount() {
      return (this.results.pages || []).filter((p) => p.crawled !== false)
        .length;
    },
    discoveredCount() {
      return (this.results.pages || []).filter((p) => p.crawled === false)
        .length;
    },
    filteredResults() {
      return (this.results.pages || []).filter((page) => {
        if (!page) return false;
        const matchTitle =
          this.filters.titleStatus === "" ||
          (this.filters.titleStatus === "ideal" && page.is_title_ideal) ||
          (this.filters.titleStatus === "short" && page.is_title_too_short) ||
          (this.filters.titleStatus === "long" &&
            !page.is_title_ideal &&
            !page.is_title_too_short) ||
          (this.filters.titleStatus === "missing" && page.is_title_missing);

        const matchMeta =
          this.filters.metaStatus === "" ||
          (this.filters.metaStatus === "missing" &&
            page.is_description_missing) ||
          (this.filters.metaStatus === "duplicate" &&
            page.is_description_duplicate) ||
          (this.filters.metaStatus === "valid" &&
            !page.is_description_missing &&
            !page.is_description_duplicate);

        const matchHttps =
          this.filters.https === "" ||
          (this.filters.https === "Yes" && page.hasHttps) ||
          (this.filters.https === "No" && !page.hasHttps);
        const matchDupTitle =
          this.filters.duplicateTitle === "" ||
          (this.filters.duplicateTitle === "Yes" && page.is_title_duplicate) ||
          (this.filters.duplicateTitle === "No" && !page.is_title_duplicate);

        const matchDupContent =
          this.filters.duplicateMainContent === "" ||
          (this.filters.duplicateMainContent === "Yes" &&
            page.is_main_content_duplicate) ||
          (this.filters.duplicateMainContent === "No" &&
            !page.is_main_content_duplicate);

        const matchSearch = page.url
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());

        return (
          matchTitle &&
          matchMeta &&
          matchHttps &&
          matchDupTitle &&
          matchDupContent &&
          matchSearch
        );
      });
    },
    sortedResults() {
      const results = [...this.filteredResults];
      if (!this.sortColumn) return results;

      const dir = this.sortDirection === "asc" ? 1 : -1;
      const col = this.sortColumn;

      return results.sort((a, b) => {
        let valA, valB;
        switch (col) {
          case "url":
            valA = (a.url || "").toLowerCase();
            valB = (b.url || "").toLowerCase();
            break;
          case "title":
            valA = (a.title || "").toLowerCase();
            valB = (b.title || "").toLowerCase();
            break;
          case "description":
            valA = (a.description || "").toLowerCase();
            valB = (b.description || "").toLowerCase();
            break;
          case "https":
            valA = a.hasHttps ? 1 : 0;
            valB = b.hasHttps ? 1 : 0;
            break;
          case "status":
            valA = a.crawled !== false ? 1 : 0;
            valB = b.crawled !== false ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    filteredIssuesToFix() {
      const pageIssues = this.currentPage?.issues || [];
      const crawlIssues = this.crawlIndexingData?.issues || [];

      const normalizedCrawlIssues = crawlIssues.map((issue) => ({
        ...issue,
        priority: issue.severity?.toUpperCase() || "MEDIUM",
        message: issue.description || "",
      }));

      const allIssues = [...pageIssues, ...normalizedCrawlIssues];

      return allIssues.filter((issue) => {
        if (this.activeTab === "Meta Data") {
          return (
            issue.section === "metadata" &&
            !issue.message.toLowerCase().includes("https")
          );
        } else if (this.activeTab === "SOP Compliance") {
          return (
            issue.section === "sop" ||
            issue.message.toLowerCase().includes("https")
          );
        } else if (this.activeTab === "Schema Markup") {
          return issue.section === "schema_markup";
        } else if (this.activeTab === "Crawl Indexing") {
          return issue.section === "crawl_indexing";
        }
        return false;
      });
    },
    // currentPage() {
    //   return this.selectedPage !== null
    //     ? this.filteredResults[this.selectedPage]
    //     : {};
    // },
    currentPage() {
      return this.selectedPage !== null
        ? this.results.pages[this.selectedPage]
        : null;
    },

    // ── Schema Markup Computed Properties ──────────────────────────
    schemaData() {
      return this.currentPage?.schema_markup || {};
    },
    schemaHasMarkup() {
      return this.schemaData.has_schema_markup || false;
    },
    schemaScore() {
      return this.schemaData.score || 0;
    },
    schemaScoreBreakdown() {
      return this.schemaData.score_breakdown || {};
    },
    schemaNaCount() {
      const breakdown = this.schemaScoreBreakdown;
      return Object.values(breakdown).filter(
        (item) => item.applicable === false
      ).length;
    },
    schemaTargetTypes() {
      return [
        "Organization",
        "LocalBusiness",
        "Product",
        "BreadcrumbList",
        "FAQPage",
        "NewsArticle",
        "Review",
        "Event",
        "Recipe",
        "VideoObject",
        "JobPosting",
        "WebSite",
      ];
    },
    /** All detected types (including supporting types like WebPage, ListItem, etc.) */
    schemaAllDetected() {
      return this.schemaData.all_detected || {};
    },
    schemaAllDetectedTypes() {
      return Object.keys(this.schemaAllDetected).sort();
    },
    /** Only the 12 recommended types, with their detection status */
    schemaRecommendedDetails() {
      const details = this.schemaData.recommended_details || {};
      const result = {};
      this.schemaTargetTypes.forEach((type) => {
        result[type] = details[type] || {
          detected: false,
          via: null,
          properties: [],
        };
      });
      return result;
    },
    schemaDetectedCount() {
      return Object.values(this.schemaRecommendedDetails).filter(
        (s) => s.detected
      ).length;
    },
    schemaMissingRec() {
      return this.schemaData.missing_recommended || {};
    },
    schemaFormatLabel() {
      const hasJsonLd = (this.schemaData.jsonld_blocks_count || 0) > 0;
      const hasMicrodata = (this.schemaData.microdata_items_count || 0) > 0;
      if (hasJsonLd && hasMicrodata) return "JSON-LD + Microdata";
      if (hasJsonLd) return "JSON-LD";
      if (hasMicrodata) return "Microdata";
      return "None";
    },

    chartData() {
      const errors = this.results.pages.filter(
        (p) =>
          p.is_description_missing ||
          p.is_title_missing ||
          (!p.is_title_ideal && !p.is_title_too_short) ||
          !p.hasHttps
      ).length;

      const warnings = this.results.pages.filter(
        (p) => p.is_title_too_short || p.is_description_duplicate
      ).length;

      const valid = this.results.pages.length - errors - warnings;

      return {
        labels: ["Valid", "Warnings", "Errors"],
        datasets: [
          {
            data: [valid, warnings, errors],
            backgroundColor: ["#22c55e", "#facc15", "#ef4444"],
          },
        ],
      };
    },
    hasIssues() {
      // return this.currentPage.issues && this.currentPage.issues.length > 0;
      return this.filteredIssuesToFix.length > 0;
    },
    allIssues() {
      if (!this.results.pages) return [];
      return this.results.pages.flatMap((page) => page.issues || []);
    },
    sortedExternalLinks() {
      const arr = this.currentPage?.external_links_validation || [];
      if (!this.externalLinksSortColumn) return arr;
      const results = [...arr];
      const dir = this.externalLinksSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.externalLinksSortColumn) {
          case "extUrl":
            valA = (a.url || "").toLowerCase();
            valB = (b.url || "").toLowerCase();
            break;
          case "extStatus":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    // Paginated External Links
    paginatedExternalLinks() {
      const arr = this.sortedExternalLinks;
      const start =
        (this.sopPagination.externalLinks - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    externalLinksTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.external_links_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedLocalLinks() {
      const arr = this.currentPage?.local_links_validation || [];
      if (!this.localLinksSortColumn) return arr;
      const results = [...arr];
      const dir = this.localLinksSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.localLinksSortColumn) {
          case "url":
            valA = (a.url || "").toLowerCase();
            valB = (b.url || "").toLowerCase();
            break;
          case "host":
            valA = (a.matched_host || "").toLowerCase();
            valB = (b.matched_host || "").toLowerCase();
            break;
          case "tag":
            valA = (a.tag || "").toLowerCase();
            valB = (b.tag || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    // Paginated Local Links
    paginatedLocalLinks() {
      const arr = this.sortedLocalLinks;
      const start = (this.sopPagination.localLinks - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    localLinksTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.local_links_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedProfanity() {
      const arr = this.currentPage?.profanity_validation || [];
      if (!this.profanitySortColumn) return arr;
      const results = [...arr];
      const dir = this.profanitySortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.profanitySortColumn) {
          case "word":
            valA = (a.word || "").toLowerCase();
            valB = (b.word || "").toLowerCase();
            break;
          case "category":
            valA = (a.category || "").toLowerCase();
            valB = (b.category || "").toLowerCase();
            break;
          case "severity":
            valA = (a.severity || "").toLowerCase();
            valB = (b.severity || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedProfanity() {
      const arr = this.sortedProfanity;
      const start = (this.sopPagination.profanity - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    profanityTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.profanity_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedMinifiedAssets() {
      const arr = this.currentPage?.minified_assets?.results || [];
      if (!this.minifiedSortColumn) return arr;
      const results = [...arr];
      const dir = this.minifiedSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.minifiedSortColumn) {
          case "file":
            valA = (a.file || "").toLowerCase();
            valB = (b.file || "").toLowerCase();
            break;
          case "type":
            valA = (a.type || "").toLowerCase();
            valB = (b.type || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedMinifiedAssets() {
      const arr = this.sortedMinifiedAssets;
      const start = (this.sopPagination.minified - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    minifiedTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.minified_assets?.results?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    contactLinkRows() {
      const rows = [];
      if (
        this.contactLinkFilter === "all" ||
        this.contactLinkFilter === "email"
      ) {
        (this.currentPage?.email_mailto_validation || []).forEach((item) => {
          rows.push({
            contact: item.email,
            type: "Email",
            href: item.href,
            status: item.status,
            reason: item.reason,
          });
        });
      }
      if (
        this.contactLinkFilter === "all" ||
        this.contactLinkFilter === "tel"
      ) {
        (this.currentPage?.tel_link_validation || []).forEach((item) => {
          rows.push({
            contact: item.number,
            type: "Telephone",
            href: item.href,
            status: item.status,
            reason: item.reason,
          });
        });
      }
      return rows;
    },
    sortedContactLinkRows() {
      const arr = this.contactLinkRows;
      if (!this.contactLinkSortColumn) return arr;
      const results = [...arr];
      const dir = this.contactLinkSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.contactLinkSortColumn) {
          case "contact":
            valA = (a.contact || "").toLowerCase();
            valB = (b.contact || "").toLowerCase();
            break;
          case "type":
            valA = (a.type || "").toLowerCase();
            valB = (b.type || "").toLowerCase();
            break;
          case "href":
            valA = (a.href || "").toLowerCase();
            valB = (b.href || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedContactLinkRows() {
      const arr = this.sortedContactLinkRows;
      const start = (this.sopPagination.contactLink - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    contactLinkTotalItems() {
      return this.contactLinkRows.length;
    },
    contactLinkTotalPages() {
      return Math.max(
        1,
        Math.ceil(this.contactLinkTotalItems / this.sopItemsPerPage)
      );
    },
    sortedImageFilenames() {
      const arr = this.currentPage?.image_filename_validation || [];
      if (!this.imageFilenameSortColumn) return arr;
      const results = [...arr];
      const dir = this.imageFilenameSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.imageFilenameSortColumn) {
          case "filename":
            valA = (a.filename || "").toLowerCase();
            valB = (b.filename || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedImageFilenames() {
      const arr = this.sortedImageFilenames;
      const start =
        (this.sopPagination.imageFilenames - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    imageFilenamesTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.image_filename_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedImageAlt() {
      const arr = this.currentPage?.image_alt_validation || [];
      if (!this.imageAltSortColumn) return arr;
      const results = [...arr];
      const dir = this.imageAltSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.imageAltSortColumn) {
          case "filename":
            valA = (a.filename || "").toLowerCase();
            valB = (b.filename || "").toLowerCase();
            break;
          case "alt":
            valA = (a.alt_text || "").toLowerCase();
            valB = (b.alt_text || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedImageAlt() {
      const arr = this.sortedImageAlt;
      const start = (this.sopPagination.imageAlt - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    imageAltTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.image_alt_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedImageSize() {
      const arr = this.currentPage?.image_size_validation || [];
      if (!this.imageSizeSortColumn) return arr;
      const results = [...arr];
      const dir = this.imageSizeSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.imageSizeSortColumn) {
          case "filename":
            valA = (a.filename || "").toLowerCase();
            valB = (b.filename || "").toLowerCase();
            break;
          case "size":
            valA = a.size_kb ?? 0;
            valB = b.size_kb ?? 0;
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : a.status === "Skipped" ? 2 : 0;
            valB = b.status === "Passed" ? 1 : b.status === "Skipped" ? 2 : 0;
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedImageSize() {
      const arr = this.sortedImageSize;
      const start = (this.sopPagination.imageSize - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    imageSizeTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.image_size_validation?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedW3CIssues() {
      const arr = this.currentPage?.w3c_validation?.results || [];
      if (!this.w3cIssuesSortColumn) return arr;
      const results = [...arr];
      const dir = this.w3cIssuesSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.w3cIssuesSortColumn) {
          case "resource":
            valA = (a.resource || "").toLowerCase();
            valB = (b.resource || "").toLowerCase();
            break;
          case "line":
            valA = a.first_line ?? 0;
            valB = b.first_line ?? 0;
            break;
          case "severity":
            valA = (a.severity || "").toLowerCase();
            valB = (b.severity || "").toLowerCase();
            break;
          case "message":
            valA = (a.message || "").toLowerCase();
            valB = (b.message || "").toLowerCase();
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedW3CIssues() {
      const arr = this.sortedW3CIssues;
      const start = (this.sopPagination.w3c - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    w3cTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.w3c_validation?.results?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedBrokenLinks() {
      const arr = this.currentPage?.broken_links?.results || [];
      if (!this.brokenLinksSortColumn) return arr;
      const results = [...arr];
      const dir = this.brokenLinksSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.brokenLinksSortColumn) {
          case "url":
            valA = (a.url || "").toLowerCase();
            valB = (b.url || "").toLowerCase();
            break;
          case "http":
            valA = a.http_status ?? 0;
            valB = b.http_status ?? 0;
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : 0;
            valB = b.status === "Passed" ? 1 : 0;
            break;
          case "message":
            valA = (a.message || "").toLowerCase();
            valB = (b.message || "").toLowerCase();
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    paginatedBrokenLinks() {
      const arr = this.sortedBrokenLinks;
      const start = (this.sopPagination.brokenLinks - 1) * this.sopItemsPerPage;
      return arr.slice(start, start + this.sopItemsPerPage);
    },
    brokenLinksTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          (this.currentPage?.broken_links?.results?.length || 0) /
            this.sopItemsPerPage
        )
      );
    },
    sortedAdditionalSop() {
      const arr = this.currentPage?.additional_sop?.results || [];
      if (!this.additionalSopSortColumn) return arr;
      const results = [...arr];
      const dir = this.additionalSopSortDirection === "asc" ? 1 : -1;
      return results.sort((a, b) => {
        let valA, valB;
        switch (this.additionalSopSortColumn) {
          case "check":
            valA = (a.check || "").toLowerCase();
            valB = (b.check || "").toLowerCase();
            break;
          case "status":
            valA = a.status === "Passed" ? 1 : a.status === "Warning" ? 2 : 0;
            valB = b.status === "Passed" ? 1 : b.status === "Warning" ? 2 : 0;
            break;
          case "message":
            valA = (a.message || "").toLowerCase();
            valB = (b.message || "").toLowerCase();
            break;
          default:
            return 0;
        }
        if (valA < valB) return -1 * dir;
        if (valA > valB) return 1 * dir;
        return 0;
      });
    },
    metadataIssues() {
      console.log(this.results.issues);

      return this.results.issues?.filter((i) => i.section === "metadata") || [];
    },
    sopIssues() {
      return this.results.issues?.filter((i) => i.section === "sop") || [];
    },
    // metadataIssues() {
    //   if (!this.currentPage || !this.currentPage.issues) return [];
    //   return this.currentPage.issues.filter((i) => i.section === "metadata");
    // },
    // sopIssues() {
    //   if (!this.currentPage || !this.currentPage.issues) return [];
    //   return this.currentPage.issues.filter((i) => i.section === "sop");
    // },
  },
  methods: {
    handleScanError(errorMessage) {
      this.scanError = errorMessage;
    },
    getExplanation(issue) {
      return issue.explanation || "";
    },
    selectPage(index) {
      const page = this.sortedResults[index];
      if (!page) {
        console.warn("Selected page not found in sortedResults.");
        return;
      }

      const originalIndex = this.results.pages.findIndex(
        (p) => p.url === page.url
      );
      if (originalIndex !== -1) {
        this.selectedPage = originalIndex;
        localStorage.setItem("selectedPageIndex", originalIndex);

        // Emit event to notify parent to fetch new audit results for this URL
        this.$emit("page-selected", page.url);

        // Auto-scroll to "Details for" section
        this.$nextTick(() => {
          if (this.$refs.detailsSection) {
            this.$refs.detailsSection.scrollIntoView({ behavior: "smooth" });
          }
        });

        // Auto-trigger SQLi scan for the selected URL
        this.runSqliLiveScan();
      } else {
        console.warn("Page not found in original results.pages.");
      }
    },

    // ── Discovered Pages: Scan Single ─────────────────────────────────

    async scanSinglePage(url) {
      if (!url || this.scanningInProgress) return;

      this.clearScanCompleteTimer();
      this.scanCompleteUrl = "";
      this.scanningInProgress = true;
      this.scanningUrl = url;

      try {
        this.$emit("scan-selected-pages", [url]);
      } catch (_) {
        this.scanningInProgress = false;
        this.scanningUrl = null;
      }
    },

    handleScanComplete() {
      const scannedUrl = this.scanningUrl;
      this.scanningInProgress = false;
      this.scanningUrl = null;

      // If the scanned page came back as skipped, show a toast instead of
      // the usual "Scan Completed" popup.
      const scannedPage = scannedUrl
        ? this.results?.pages?.find((p) => p.url === scannedUrl)
        : null;

      if (
        scannedPage &&
        scannedPage.crawled === false &&
        scannedPage.scan_status === "skipped"
      ) {
        this.scanCompleteUrl = "";
        this.scanSkippedMessage =
          scannedPage.scan_message ||
          "This page was not crawled because it is an external page.";
        this.clearScanCompleteTimer();
        this.scanCompleteTimer = setTimeout(() => {
          this.dismissPopup();
        }, 10000);
        return;
      }

      if (scannedUrl) {
        this.scanSkippedMessage = "";
        this.scanCompleteUrl = scannedUrl;
        // Auto-remove after 10 seconds with swipe animation
        this.clearScanCompleteTimer();
        this.scanCompleteTimer = setTimeout(() => {
          this.dismissPopup();
        }, 10000);
      }
    },

    clearScanCompleteTimer() {
      if (this.scanCompleteTimer) {
        clearTimeout(this.scanCompleteTimer);
        this.scanCompleteTimer = null;
      }
    },

    dismissPopup() {
      this.clearScanCompleteTimer();
      this.scanCompleteUrl = "";
      this.scanSkippedMessage = "";
    },

    handleScanSingleError() {
      this.scanningInProgress = false;
      this.scanningUrl = null;
    },

    // ── Table sorting ───────────────────────────────────────────

    toggleSort(column) {
      if (this.sortColumn === column) {
        if (this.sortDirection === "asc") {
          this.sortDirection = "desc";
        } else if (this.sortDirection === "desc") {
          this.sortColumn = null;
          this.sortDirection = "asc";
        }
      } else {
        this.sortColumn = column;
        this.sortDirection = "asc";
      }
    },
    toggleExternalLinksSort(column) {
      if (this.externalLinksSortColumn === column) {
        if (this.externalLinksSortDirection === "asc") {
          this.externalLinksSortDirection = "desc";
        } else if (this.externalLinksSortDirection === "desc") {
          this.externalLinksSortColumn = null;
          this.externalLinksSortDirection = "asc";
        }
      } else {
        this.externalLinksSortColumn = column;
        this.externalLinksSortDirection = "asc";
      }
    },
    toggleLocalLinksSort(column) {
      if (this.localLinksSortColumn === column) {
        if (this.localLinksSortDirection === "asc") {
          this.localLinksSortDirection = "desc";
        } else if (this.localLinksSortDirection === "desc") {
          this.localLinksSortColumn = null;
          this.localLinksSortDirection = "asc";
        }
      } else {
        this.localLinksSortColumn = column;
        this.localLinksSortDirection = "asc";
      }
    },
    toggleProfanitySort(column) {
      if (this.profanitySortColumn === column) {
        if (this.profanitySortDirection === "asc") {
          this.profanitySortDirection = "desc";
        } else if (this.profanitySortDirection === "desc") {
          this.profanitySortColumn = null;
          this.profanitySortDirection = "asc";
        }
      } else {
        this.profanitySortColumn = column;
        this.profanitySortDirection = "asc";
      }
    },
    toggleTableSort(columnKey, directionKey, column) {
      if (this[columnKey] === column) {
        if (this[directionKey] === "asc") {
          this[directionKey] = "desc";
        } else if (this[directionKey] === "desc") {
          this[columnKey] = null;
          this[directionKey] = "asc";
        }
      } else {
        this[columnKey] = column;
        this[directionKey] = "asc";
      }
    },

    // ── SQLi Live Scanner ──────────────────────────────────────────

    async runSqliLiveScan() {
      // Prevent concurrent scans
      if (this.sqliScanning) return;

      this.sqliScanning = true;
      this.sqliScanError = "";

      // Clear previous results when re-scanning a different URL
      this.sqliLiveResults = null;

      const token = localStorage.getItem("token");
      if (!token) {
        this.sqliScanError = "Authentication required. Please log in.";
        this.sqliScanning = false;
        return;
      }

      // Use the currently selected page URL, falling back to website URL
      const targetUrl =
        this.currentPage?.url ||
        this.results?.website?.website_url ||
        this.results?.pages?.[0]?.url ||
        "";

      if (!targetUrl) {
        this.sqliScanError = "No URL available to scan.";
        this.sqliScanning = false;
        return;
      }

      try {
        const response = await axios.post(
          `${baseUrl}/sql-injection-scan`,
          { url: targetUrl },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 330000, // 5.5 minutes — slightly above backend 300s
          }
        );
        this.sqliLiveResults = response.data;
      } catch (err) {
        console.error("SQLi live scan failed:", err);
        if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
          this.sqliScanError =
            "The scan is taking longer than expected. The page may have many forms or slow responses. Please try again with a different URL.";
        } else {
          this.sqliScanError =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Scan request failed. Please try again.";
        }
      } finally {
        this.sqliScanning = false;
      }
    },

    /** Check if a form has any vulnerability findings (non-"None" severity) */
    hasFormVulnerability(form) {
      if (!form?.payloads) return false;
      return form.payloads.some(
        (p) =>
          p.severity &&
          p.severity !== "None" &&
          p.result !== "No Vulnerability Detected"
      );
    },

    /** Get only the payloads that resulted in a vulnerability for a form */
    getVulnerablePayloads(form) {
      if (!form?.payloads) return [];
      return form.payloads.filter(
        (p) =>
          p.severity &&
          p.severity !== "None" &&
          p.result !== "No Vulnerability Detected"
      );
    },

    /** Truncate long result strings for compact display */
    truncateResult(result) {
      if (!result) return "—";
      if (result.length > 28) return result.substring(0, 25) + "…";
      return result;
    },

    handleAccessLogUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.accessLogFile = file.name;
      this.accessLogError = "";
      this.accessLogSuccess = "";
      this.accessLogUploading = true;

      this.$emit("upload-access-log", file);
    },

    exportToCSV() {
      const rows = this.filteredResults.map((page) => ({
        URL: page.url,
        Title: page.title,
        MetaDescription: page.description,
        HTTPS: page.hasHttps,
        DuplicateTitle: page.is_title_duplicate ? "Yes" : "No",
        TitleMissing: page.is_title_missing ? "Yes" : "No",
        Phone: page.phone_numbers,
        Email: page.email_address,
        company_name: page.company_name,
        PhoneFormatValid: page.sop?.phone_format_valid ? "Yes" : "No",
        EmailFormatValid: page.sop?.email_format_valid ? "Yes" : "No",
        AddressFormatValid: page.sop?.address_format_valid ? "Yes" : "No",
        CompanyNameValid: page.sop?.company_name_valid ? "Yes" : "No",
        CopyrightValid: page.sop?.copyright_year_valid ? "Yes" : "No",
        EmailMailtoValid: page.sop?.email_mailto_valid ? "Yes" : "No",
        EmailMailtoFailedCount: (page.email_mailto_validation || []).filter(
          (e) => e.status === "Failed"
        ).length,
        TelLinkValid: page.sop?.tel_link_valid ? "Yes" : "No",
        TelLinkFailedCount: (page.tel_link_validation || []).filter(
          (e) => e.status === "Failed"
        ).length,
        BackToTopValid: page.sop?.back_to_top_valid ? "Yes" : "No",
        ExternalLinksValid: page.sop?.external_links_valid ? "Yes" : "No",
        ExternalLinksFailedCount: (page.external_links_validation || []).filter(
          (e) => e.status === "Failed"
        ).length,
        LocalLinksValid: page.sop?.local_links_valid !== false ? "Yes" : "No",
        LocalLinksFailedCount: (page.local_links_validation || []).filter(
          (e) => e.status === "Failed"
        ).length,
        LocalLinksHosts: [
          ...new Set(
            (page.local_links_validation || [])
              .map((e) => e.matched_host)
              .filter(Boolean)
          ),
        ].join("; "),
        ProfanityValid: page.sop?.profanity_valid !== false ? "Yes" : "No",
        ProfanityFailedCount: (page.profanity_validation || []).filter(
          (e) => e.status === "Failed"
        ).length,
        ProfanityWords: [
          ...new Set(
            (page.profanity_validation || []).map((e) => e.word).filter(Boolean)
          ),
        ].join("; "),
        MinifiedAssetsScore: page.minified_assets?.score ?? 100,
        MinifiedAssetsValid: page.sop?.minified_assets_valid ? "Yes" : "No",
        MinifiedAssetsIssues: page.minified_assets?.summary?.issues ?? 0,
        ImageFilenamesValid: page.sop?.image_filenames_valid ? "Yes" : "No",
        ImageFilenamesFailedCount: (
          page.image_filename_validation || []
        ).filter((e) => e.status === "Failed").length,
        W3CScore: page.w3c_validation?.score ?? 100,
        W3CValid: page.sop?.w3c_valid ? "Yes" : "No",
        W3CErrors: page.w3c_validation?.summary?.errors ?? 0,
        W3CWarnings: page.w3c_validation?.summary?.warnings ?? 0,
        AdditionalSOPScore: page.additional_sop?.score ?? 100,
        AdditionalSOPValid: page.sop?.additional_sop_valid ? "Yes" : "No",
        BrokenLinksScore: page.broken_links?.score ?? 100,
        BrokenLinksValid: page.sop?.broken_links_valid ? "Yes" : "No",
        BrokenLinksFailed: page.broken_links?.summary?.failed ?? 0,
      }));

      if (rows.length === 0) {
        alert("No data available to export.");
        return;
      }

      const csv = [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) =>
          Object.values(row)
            .map((val) => `"${val}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // link.download = "validation_results.csv";
      // Get company name from the first page that has it
      const companyName =
        this.filteredResults.find((p) => p.company_name)?.company_name ||
        "website";

      const sanitizedCompany = companyName.substring(0, 40); // optional length limit

      link.download = `${sanitizedCompany} Scanned Results.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
  watch: {
    activeTab(newTab, oldTab) {
      // Auto-trigger live SQLi scan when the SQL Injection tab is first opened
      if (
        newTab === "SQL Injection" &&
        oldTab !== "SQL Injection" &&
        !this.sqliLiveResults &&
        !this.sqliScanning
      ) {
        this.runSqliLiveScan();
      }
    },
    /** When results change (e.g. history item selected), populate cached SQLi scan */
    results: {
      handler(val) {
        if (val && val.sqli_scan && !this.sqliLiveResults) {
          this.sqliLiveResults = val.sqli_scan;
        }
        // Clear scan tracking when new results come in
        this.scanningInProgress = false;
        this.scanningUrl = null;
      },
      deep: true,
    },
    googlebotLogData(val) {
      if (val !== null) {
        this.accessLogUploading = false;
        if (val.has_googlebot_data) {
          this.accessLogSuccess =
            `Found ${val.summary.total_visits} Googlebot visits. ` +
            `Last crawled: ${val.summary.last_crawl}.`;
          this.accessLogError = "";
        } else {
          this.accessLogError = "No Googlebot activity found in this log file.";
          this.accessLogSuccess = "";
        }
      }
    },
  },
  mounted() {
    const savedIndex = localStorage.getItem("selectedPageIndex");
    if (savedIndex) {
      this.selectedPage = parseInt(savedIndex);
    }
  },
  beforeUnmount() {
    this.clearScanCompleteTimer();
  },
};
</script>
<style scoped>
.filters {
  background: #f0f7fe;
  padding-inline: 0.5rem;
  border: none;
}
th {
  font-weight: 500;
  background: #f0f7fe;
}
.resource-audit-content {
  animation: fadeIn 0.4s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Popup swipe transition */
.popup-slide-enter-active,
.popup-slide-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}
.popup-slide-enter-from {
  transform: translateX(120%);
  opacity: 0;
}
.popup-slide-leave-to {
  transform: translateX(120%);
  opacity: 0;
}
</style>
